// src/pages/ccp/BakingCcpLivePage.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Link, useParams, useSearchParams, useNavigate } from 'react-router-dom';
import bakingCcpService from '../../services/bakingCcpService';
import logService from '../../services/logService';
import companyService from '../../services/companyService';
import productLabelService from '../../services/productLabelService';
import sqfService from '../../services/sqfService';
import LabelRenderer from '../../components/LabelRenderer';
import StartVisualInspectionModal from '../../components/StartVisualInspectionModal';
import VisualInspectionsList from '../../components/VisualInspectionsList';
import { useLanguage, t } from '../../context/LanguageContext';
import { getCcpFlowLabels } from '../../config/ccpFlowLabels';

// Splits a label snapshot into { design, content } — same pattern as LabelDetailPage
function splitSnapshot(snapshot) {
  if (!snapshot) return { design: null, content: {} };
  const { content = {}, ...design } = snapshot;
  return { design, content };
}

const EXP_LABEL_DISPLAY = {
  best_by: 'Best By', use_by: 'Use By', exp: 'Exp.',
  best_if_used_by: 'Best If Used By', custom: 'Custom',
};

const MONTH_ABBR = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
function formatLabelDate(str, fmt) {
  if (!str) return '';
  try {
    const [y, m, d] = str.slice(0, 10).split('-');
    if (!y || !m || !d) return str;
    if (fmt === 'DD/MM/YYYY') return `${d}/${m}/${y}`;
    if (fmt === 'YYYY-MM-DD') return `${y}-${m}-${d}`;
    if (fmt === 'DD-MMM-YYYY') return `${d}-${MONTH_ABBR[parseInt(m, 10) - 1]}-${y}`;
    return `${m}/${d}/${y}`;
  } catch { return str; }
}

// ── Live Print Modal ──────────────────────────────────────────────────────────
// Shown when packaging team clicks "Print Label" from the Blast Out Totals table.
// All variable fields (lot, production date, expiration) are pre-filled from the run.
function LivePrintModal({ show, onClose, product, lotCode, productionDate, labelTemplate, initialDateFormat, onPrint }) {
  const { lang } = useLanguage();
  const variants = labelTemplate?.Variants || [];
  const [expirationDate, setExpirationDate] = useState('');
  const [dateFormat, setDateFormat] = useState('MM/DD/YYYY');
  const [copies, setCopies] = useState(1);
  const [notes, setNotes] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState('');
  const [selectedVariantId, setSelectedVariantId] = useState(null);

  // Restore last-used variant from session on modal open
  useEffect(() => {
    if (!show || !labelTemplate?.id) return;
    const stored = sessionStorage.getItem(`lastVariant_${labelTemplate.id}`);
    if (stored === 'base' || stored === null) setSelectedVariantId(null);
    else {
      const parsed = parseInt(stored, 10);
      setSelectedVariantId(isNaN(parsed) ? null : parsed);
    }
  }, [show, labelTemplate?.id]);

  const handleVariantSelect = (variantId) => {
    setSelectedVariantId(variantId);
    if (labelTemplate?.id) {
      sessionStorage.setItem(`lastVariant_${labelTemplate.id}`, variantId === null ? 'base' : String(variantId));
    }
  };

  // Sync dateFormat from the remembered (or template default) value whenever modal opens
  useEffect(() => {
    if (show) setDateFormat(initialDateFormat || 'MM/DD/YYYY');
  }, [show, initialDateFormat]);

  // Auto-calculate expiration from shelf life whenever productionDate changes
  useEffect(() => {
    if (!show) return;
    if (productionDate && product?.shelfLifeDays) {
      const d = new Date(productionDate + 'T00:00:00');
      d.setDate(d.getDate() + product.shelfLifeDays);
      setExpirationDate(d.toISOString().split('T')[0]);
    } else {
      setExpirationDate('');
    }
  }, [show, productionDate, product?.shelfLifeDays, product?.id]);

  const handleClose = () => {
    setExpirationDate(''); setDateFormat('MM/DD/YYYY'); setCopies(1); setNotes(''); setError('');
    onClose();
  };

  const handleConfirm = async () => {
    setConfirming(true);
    setError('');
    try {
      await productLabelService.logPrintJob(labelTemplate.id, {
        lotCode: lotCode || null,
        productionDate: productionDate || null,
        expirationDate: expirationDate || null,
        notes: notes || null,
        copies,
        variantId: selectedVariantId || null,
      });
      onPrint({ lotCode, productionDate, expirationDate, dateFormat, copies, notes, variantId: selectedVariantId || null });
      handleClose();
    } catch (err) {
      setError(err?.response?.data?.message || t('Failed to log print job.', 'Error al registrar el trabajo de impresión.', lang));
    } finally {
      setConfirming(false);
    }
  };

  if (!show) return null;
  return (
    <>
      <div className="modal-backdrop fade show" style={{ zIndex: 1040 }} onClick={handleClose} />
      <div className="modal fade show d-block" style={{ zIndex: 1050 }} tabIndex={-1} role="dialog" aria-modal="true">
        <div className="modal-dialog modal-dialog-centered" >
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" style={{ fontSize: 15 }}>
                {t('Print Label', 'Imprimir Etiqueta', lang)} — {product?.name}
              </h5>
              <button type="button" className="btn-close" onClick={handleClose} disabled={confirming} />
            </div>
            <div className="modal-body">
              {error && <div className="alert alert-danger py-2 mb-3" style={{ fontSize: 13 }}>{error}</div>}

              <div className="alert alert-info py-2 mb-3" style={{ fontSize: 12 }}>
                {t('Pre-filled from the active production run.', 'Pre-llenado desde la corrida de producción activa.', lang)}{' '}
                {t('To change the expiration, update shelf life in', 'Para cambiar la caducidad, actualiza la vida útil en', lang)}{' '}
                <strong>{t('Product Settings', 'Configuración de Producto', lang)}</strong>, {t('or use the', 'o usa el', lang)}{' '}
                <Link to="/labels" onClick={handleClose}>{t('Label Module', 'Módulo de Etiquetas', lang)}</Link>{' '}
                {t('for full options.', 'para opciones completas.', lang)}
              </div>

              {variants.length > 0 && (
                <div className="mb-3">
                  <label className="form-label fw-semibold mb-1" style={{ fontSize: 13 }}>{t('Presentation', 'Presentación', lang)}</label>
                  <div className="d-flex flex-wrap gap-2">
                    <button
                      type="button"
                      className={`btn btn-sm ${selectedVariantId === null ? 'btn-primary' : 'btn-outline-secondary'}`}
                      onClick={() => handleVariantSelect(null)}
                    >
                      {labelTemplate?.baseVariantName || t('Base Label', 'Etiqueta Base', lang)}
                    </button>
                    {variants.map(v => (
                      <button
                        key={v.id}
                        type="button"
                        className={`btn btn-sm ${selectedVariantId === v.id ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => handleVariantSelect(v.id)}
                      >
                        {v.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="row g-2 mb-3">
                <div className="col-6">
                  <label className="form-label fw-semibold mb-1" style={{ fontSize: 13 }}>{t('Lot Code', 'Código de Lote', lang)}</label>
                  <input className="form-control form-control-sm" value={lotCode || '—'} readOnly />
                </div>
                <div className="col-6">
                  <label className="form-label fw-semibold mb-1" style={{ fontSize: 13 }}>{t('Production Date', 'Fecha de Producción', lang)}</label>
                  <input className="form-control form-control-sm" value={productionDate || '—'} readOnly />
                </div>
              </div>

              <div className="row g-2 mb-3">
                <div className="col-6">
                  <label className="form-label fw-semibold mb-1" style={{ fontSize: 13 }}>
                    {t('Expiration Date', 'Fecha de Caducidad', lang)}
                    {product?.shelfLifeDays && (
                      <span className="text-muted fw-normal ms-1" style={{ fontSize: 11 }}>
                        ({product.shelfLifeDays}d {t('shelf life', 'vida útil', lang)})
                      </span>
                    )}
                  </label>
                  <input type="date" className="form-control form-control-sm"
                    value={expirationDate} onChange={e => setExpirationDate(e.target.value)} />
                </div>
                <div className="col-6">
                  <label className="form-label fw-semibold mb-1" style={{ fontSize: 13 }}>{t('Copies', 'Copias', lang)}</label>
                  <input type="number" className="form-control form-control-sm" min={1} max={100}
                    value={copies} onChange={e => setCopies(Math.max(1, parseInt(e.target.value) || 1))} />
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label fw-semibold mb-1" style={{ fontSize: 13 }}>{t('Date Format', 'Formato de Fecha', lang)}</label>
                <select className="form-select form-select-sm" value={dateFormat} onChange={e => setDateFormat(e.target.value)}>
                  <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                  <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                  <option value="DD-MMM-YYYY">DD-MMM-YYYY</option>
                  <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                </select>
              </div>

              <div>
                <label className="form-label fw-semibold mb-1" style={{ fontSize: 13 }}>
                  {t('Notes', 'Notas', lang)} <span className="text-muted fw-normal">({t('optional', 'opcional', lang)})</span>
                </label>
                <textarea className="form-control form-control-sm" rows={2}
                  value={notes} onChange={e => setNotes(e.target.value)} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline-secondary btn-sm" onClick={handleClose} disabled={confirming}>{t('Cancel', 'Cancelar', lang)}</button>
              <button className="btn btn-primary btn-sm" onClick={handleConfirm} disabled={confirming}>
                {confirming
                  ? <><span className="spinner-border spinner-border-sm me-1" role="status" />{t('Preparing…', 'Preparando…', lang)}</>
                  : `🖨 ${t('Print', 'Imprimir', lang)}`}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default function BakingCcpLivePage() {
  const { lang } = useLanguage();
  const { runId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const initialMode = (searchParams.get('mode') || 'baking').toLowerCase();
  const [mode, setMode] = useState(initialMode === 'packaging' ? 'packaging' : 'baking');

  const [loading, setLoading] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [run, setRun] = useState(null);
  const [error, setError] = useState('');

  // feedback banners
  const [successMsg, setSuccessMsg] = useState('');
  const [warnMsg, setWarnMsg] = useState('');

  // products for cart creation
  const [products, setProducts] = useState([]);

  // Visual Inspection — mid-production AI-assisted QA spot check (SQF Hub)
  const [showVisualInspectionModal, setShowVisualInspectionModal] = useState(false);
  const [visualInspections, setVisualInspections] = useState([]);

  // temp modal
  const [showTempModal, setShowTempModal] = useState(false);
  const [tempF, setTempF] = useState('');
  const [tempCartId, setTempCartId] = useState('');
  const [savingTemp, setSavingTemp] = useState(false);

  // cart modal
  const [showCartModal, setShowCartModal] = useState(false);
  const [editingCartId, setEditingCartId] = useState(null);
  const [cartProductId, setCartProductId] = useState('');
  const [cartUnits, setCartUnits] = useState('');
  const [cartNotes, setCartNotes] = useState('');
  const [savingCart, setSavingCart] = useState(false);

  // stop-pasteurizing modal (pasteurization only — requires a final temperature)
  const [showStopPasteurizeModal, setShowStopPasteurizeModal] = useState(false);
  const [stopTempF, setStopTempF] = useState('');
  const [savingStop, setSavingStop] = useState(false);

  // edit times modal — corrects ovenOutAt/blastInAt/blastOutAt after the fact
  const [editTimesCart, setEditTimesCart] = useState(null);
  const [editTimesForm, setEditTimesForm] = useState({ ovenOutAt: '', blastInAt: '', blastOutAt: '' });
  const [editTimesNote, setEditTimesNote] = useState('');
  const [savingEditTimes, setSavingEditTimes] = useState(false);

  // packaging complete modal
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showBlockingModal, setShowBlockingModal] = useState(false);
  const [finLot, setFinLot] = useState('');
  // eslint-disable-next-line no-unused-vars
  const [qtyProduced, setQtyProduced] = useState('');
  const [packNotes, setPackNotes] = useState('');
  const [deviation, setDeviation] = useState(false);
  const [deviationNotes, setDeviationNotes] = useState('');
  const [correctiveNotes, setCorrectiveNotes] = useState('');
  const [answers, setAnswers] = useState({});
  const [savingComplete, setSavingComplete] = useState(false);
  const [editableProductTotals, setEditableProductTotals] = useState([]);

  // SQF production gates
  const [gateStatus, setGateStatus] = useState({ gated: false, pending: [] });
  const [afterGateStatus, setAfterGateStatus] = useState({ gated: false, pending: [] });
  const [preparingGate, setPreparingGate] = useState(false);

  const LOCK_STALE_MS = 5 * 60 * 1000;
  const isLockFresh = (fillingStartedAt) =>
    fillingStartedAt && Date.now() - new Date(fillingStartedAt).getTime() < LOCK_STALE_MS;

  const handleGateBannerClick = async (pending, setStatus) => {
    const item = pending[0];
    if (!item) return;
    if (item.instanceId) {
      navigate(`/sqf/fill/${item.instanceId}`);
    } else {
      setPreparingGate(true);
      try {
        const result = await sqfService.prepareProductionGate();
        setStatus(result);
        const first = result.pending[0];
        if (first?.instanceId) navigate(`/sqf/fill/${first.instanceId}`);
      } catch { /* ignore */ } finally {
        setPreparingGate(false);
      }
    }
  };

  // countdown tick (packaging card updates every minute)
  const [tick, setTick] = useState(0);

  // collapsed completed carts
  const [showCompletedCarts, setShowCompletedCarts] = useState(false);

  // label quick-print
  const [labelTemplates, setLabelTemplates] = useState([]);
  const [companyLogoUrl, setCompanyLogoUrl] = useState('');
  const lastUsedDateFormats = useRef({}); // { [templateId]: 'MM/DD/YYYY' | ... } — session-only memory
  const [livePrintTarget, setLivePrintTarget] = useState(null); // { product, labelTemplate } — labelTemplate has full snapshot
  const [loadingPrintTemplate, setLoadingPrintTemplate] = useState(false);
  const [printJob, setPrintJob] = useState(null); // triggers portal render
  const [expandedProductLabels, setExpandedProductLabels] = useState(new Set()); // productIds with product label section open

  const clearBanners = () => {
    setError('');
    setSuccessMsg('');
    setWarnMsg('');
  };

  const setModeAndUrl = (next) => {
    const n = next === 'packaging' ? 'packaging' : 'baking';
    setMode(n);
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      p.set('mode', n);
      return p;
    });
  };

  const fetchAll = async ({ silent = false } = {}) => {
    if (!silent) {
      setLoading(true);
      clearBanners();
    }

    try {
      const [cfgRes, productsRes, templatesRes, companyRes, gateRes, afterGateRes] = await Promise.all([
        bakingCcpService.getConfig().catch(() => null),
        logService.getProducts().catch(() => []),
        productLabelService.getTemplates().catch(() => []),
        companyService.getCompanies().catch(() => null),
        sqfService.getProductionGate().catch(() => ({ gated: false, pending: [] })),
        sqfService.getAfterProductionGate().catch(() => ({ gated: false, pending: [] })),
      ]);
      setGateStatus(gateRes);
      setAfterGateStatus(afterGateRes);

      setCfg(cfgRes?.config || null);
      setProducts(Array.isArray(productsRes) ? productsRes : []);
      setLabelTemplates(Array.isArray(templatesRes) ? templatesRes : []);
      if (companyRes) {
        const co = Array.isArray(companyRes) ? companyRes[0] : companyRes;
        if (co?.logoUrl) setCompanyLogoUrl(co.logoUrl);
      }

      try {
        const liveRes = await bakingCcpService.getRunLive(runId);
        setRun(liveRes?.run || null);
      } catch (e) {
        const runRes = await bakingCcpService.getRun(runId);
        setRun(runRes?.run || null);
      }
    } catch (e) {
      console.error(e);
      setError(t('Failed to load live production run.', 'Error al cargar la corrida de producción en vivo.', lang));
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId]);

  // No productId filter — a run can schedule several products, so this shows every Visual
  // Inspection recorded against the batch regardless of which product it was for.
  useEffect(() => {
    if (!run?.batchId) return;
    sqfService.getChecklistLogsForBatch(run.batchId).then(setVisualInspections).catch(() => setVisualInspections([]));
  }, [run?.batchId]);

  useEffect(() => {
    const t = setInterval(() => fetchAll({ silent: true }), 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId]);

  // Re-check gates on tab focus and every 60s tick so banner clears for all users
  const refreshGates = useCallback(() => {
    Promise.all([
      sqfService.getProductionGate().catch(() => ({ gated: false, pending: [] })),
      sqfService.getAfterProductionGate().catch(() => ({ gated: false, pending: [] })),
    ]).then(([gateRes, afterGateRes]) => {
      setGateStatus(gateRes);
      setAfterGateStatus(afterGateRes);
    });
  }, []);

  useEffect(() => {
    const handleVisibility = () => { if (document.visibilityState === 'visible') refreshGates(); };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [refreshGates]);

  useEffect(() => { refreshGates(); }, [tick, refreshGates]);

  useEffect(() => {
    const t = setInterval(() => setTick(x => x + 1), 60000);
    return () => clearInterval(t);
  }, []);

  // flowType is snapshotted per-run (authoritative once a run exists); falls back to the
  // company's current config before a run is loaded. See backend FLOW_STAGES for the source of
  // truth this mirrors.
  const flowType = run?.flowType || cfg?.flowType || 'baking';
  const isPasteurization = flowType === 'pasteurization';
  const L = getCcpFlowLabels(flowType);

  const runStatus = run?.status || '';
  const isBaking = isPasteurization ? runStatus === 'PASTEURIZING' : runStatus === 'BAKING';
  const isPaused = isPasteurization ? runStatus === 'PASTEURIZING_PAUSED' : runStatus === 'BAKING_PAUSED';
  // Pasteurization-only: the aging hold, and the post-aging window where churn batches (carts)
  // get created — this is pasteurization's equivalent of baking's active BAKING window.
  const isAging = runStatus === 'AGING';
  const isReadyToChurn = runStatus === 'READY_TO_CHURN';
  // "Stopped" = stage 1 finished, eligible to complete packaging. Baking reaches this directly;
  // pasteurization reaches it via stop → AGING → end-aging → READY_TO_CHURN.
  const isStopped = isPasteurization ? isReadyToChurn : runStatus === 'BAKING_STOPPED';
  const isCompleted = runStatus === 'COMPLETED';
  const isVerified = !!run?.verifiedAt;
  const isNoBake = !!run?.noBake;
  const statusLabel = isNoBake
    ? { BAKING: 'PRODUCING', BAKING_PAUSED: 'PAUSED', BAKING_STOPPED: 'STOPPED', COMPLETED: 'COMPLETED' }[runStatus] || runStatus
    : isAging ? L.agingLabel.en
    : isReadyToChurn ? L.readyToChurnLabel.en
    : runStatus;

  const canRecordTemp = mode === 'baking' && isBaking && !isVerified && !isCompleted;
  const canCreateCart = mode === 'baking' && (isPasteurization ? isReadyToChurn : (isBaking || isPaused)) && !isVerified && !isCompleted;
  const canStopBaking = (isBaking || isPaused) && !isVerified && !isCompleted;
  const canPause = isBaking && !isVerified && !isCompleted;
  // Pasteurization can't resume from AGING/READY_TO_CHURN — once aging starts there's no
  // sensible "resume pasteurizing" (matches backend resumeFrom).
  const canResume = isPasteurization
    ? (isPaused && !isVerified && !isCompleted)
    : ((isPaused || isStopped) && !isVerified && !isCompleted);
  const canEndAging = isPasteurization && isAging && !isVerified && !isCompleted;

  const maxMinutes = cfg?.maxMinutesBetweenTemps ?? 60;
  const maxMinutesToFreezer = cfg?.maxMinutesToFreezer ?? 360;

  // Pasteurization CCP critical limit is temperature held for a minimum duration, not just a
  // point-in-time reading — this tracks elapsed hold time against the configured requirement.
  const requiredHoldMinutes = cfg?.pasteurizationHoldMinutes ? Number(cfg.pasteurizationHoldMinutes) : null;
  const holdMinutesElapsed = useMemo(() => {
    if (!isPasteurization || !run?.bakingStartedAt) return null;
    const startMs = new Date(run.bakingStartedAt).getTime();
    if (!Number.isFinite(startMs)) return null;
    return Math.floor((Date.now() - startMs) / 60000);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPasteurization, run?.bakingStartedAt, tick]);
  const holdTimeMet = requiredHoldMinutes == null || (holdMinutesElapsed != null && holdMinutesElapsed >= requiredHoldMinutes);

  // Aging elapsed time vs configured min/max (pasteurization only, both optional).
  const minAgingHours = cfg?.minAgingHours != null ? Number(cfg.minAgingHours) : null;
  const maxAgingHours = cfg?.maxAgingHours != null ? Number(cfg.maxAgingHours) : null;
  const agingHoursElapsed = useMemo(() => {
    if (!isPasteurization || !run?.agingStartedAt) return null;
    const startMs = new Date(run.agingStartedAt).getTime();
    if (!Number.isFinite(startMs)) return null;
    return (Date.now() - startMs) / 3600000;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPasteurization, run?.agingStartedAt, tick]);
  const agingUnderMin = minAgingHours != null && agingHoursElapsed != null && agingHoursElapsed < minAgingHours;
  const agingOverMax = maxAgingHours != null && agingHoursElapsed != null && agingHoursElapsed > maxAgingHours;

  const carts = useMemo(() => {
    const list = Array.isArray(run?.Carts) ? run.Carts : [];
    return [...list].sort((a, b) => (a.cartNumber || 0) - (b.cartNumber || 0));
  }, [run]);

  const activeCarts = useMemo(() => carts.filter(c => !c?.blastOutAt), [carts]);
  const completedCarts = useMemo(() => carts.filter(c => !!c?.blastOutAt), [carts]);

  const lastTempAt = useMemo(() => {
    const list = Array.isArray(run?.TempReadings) ? run.TempReadings : [];
    if (list.length === 0) return null;
    const sorted = [...list].sort((a, b) => new Date(a.readingAt) - new Date(b.readingAt));
    const last = sorted[sorted.length - 1];
    return last?.readingAt ? new Date(last.readingAt) : null;
  }, [run]);

  const minutesSinceLast = useMemo(() => {
    if (!lastTempAt) return null;
    return Math.floor((Date.now() - lastTempAt.getTime()) / 60000);
  }, [lastTempAt]);

  const isOverdue = isBaking && minutesSinceLast != null && minutesSinceLast >= maxMinutes;
  const minsUntilNextTemp = useMemo(() => {
    if (!isBaking) return null;
    if (minutesSinceLast == null) return maxMinutes; // no readings yet, full interval available
    return maxMinutes - minutesSinceLast;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBaking, minutesSinceLast, maxMinutes, tick]);
  const isApproaching = minsUntilNextTemp != null && minsUntilNextTemp <= 5 && minsUntilNextTemp > 0;

  const cartsOverdueFreezer = useMemo(() => {
    const nowMs = Date.now();
    return carts.filter(c => {
      if (!c?.ovenOutAt) return false;
      if (c?.blastOutAt) return false;
      const ovenOutMs = new Date(c.ovenOutAt).getTime();
      if (!Number.isFinite(ovenOutMs)) return false;
      return Math.floor((nowMs - ovenOutMs) / 60000) > maxMinutesToFreezer;
    });
  }, [carts, maxMinutesToFreezer]);

  const runLot = run?.Batch?.lotCode || (run?.batchId ? `Batch #${run.batchId}` : '—');
  const runProdDate = run?.Batch?.production_date || '—';

  const productById = useMemo(() => {
    const m = new Map();
    (products || []).forEach(p => m.set(Number(p.id), p));
    return m;
  }, [products]);

  // Only products matching this run's flow are selectable in the New/Edit Batch modal —
  // same rule as the Start Production picker, so a pasteurization run can't pick up a baked
  // product (or vice versa). Products default to 'baking' if never set.
  const flowProducts = useMemo(() => {
    return (products || []).filter(p => (p.productionFlowType || 'baking') === flowType);
  }, [products, flowType]);

  // Active label templates per product — keyed by productId, value { product: tpl|null, case: tpl|null }
  // Also tracks duplicate conflicts (same product + type with multiple approved labels).
  const labelByProductId = useMemo(() => {
    const m = new Map();
    labelTemplates.forEach(tpl => {
      if (!tpl.activeVersionId) return;
      if (tpl.status === 'archived') return;
      const pid = Number(tpl.productId);
      const type = tpl.labelType === 'case' ? 'case' : 'product';
      if (!m.has(pid)) m.set(pid, { product: null, case: null, conflicts: [] });
      const entry = m.get(pid);
      if (entry[type]) {
        // Duplicate — record conflict but keep the first (most recently approved)
        entry.conflicts.push({ type, name: tpl.name });
      } else {
        entry[type] = tpl;
      }
    });
    return m;
  }, [labelTemplates]);

  // Called by LivePrintModal after job is logged — triggers portal render + window.print()
  const handleLivePrint = (jobData) => {
    const tplId = livePrintTarget?.labelTemplate?.id;
    if (tplId && jobData.dateFormat) lastUsedDateFormats.current[tplId] = jobData.dateFormat;
    setPrintJob({ ...jobData, labelTemplate: livePrintTarget?.labelTemplate });
    setTimeout(() => {
      window.print();
      setPrintJob(null);
    }, 300);
  };

  // Products selected at run start — used for quick-select buttons in cart modal.
  // Falls back to the single primary product for runs created before this field existed.
  const scheduledProducts = useMemo(() => {
    const ids = Array.isArray(run?.scheduledProductIdsJson) && run.scheduledProductIdsJson.length > 0
      ? run.scheduledProductIdsJson
      : run?.productId ? [run.productId] : [];
    return ids.map(id => productById.get(Number(id))).filter(Boolean);
  }, [run?.scheduledProductIdsJson, run?.productId, productById]);

  const freezerCarts = useMemo(() => {
    return carts.filter(c => c?.blastInAt && !c?.blastOutAt);
  }, [carts]);

  const blastOutTotalsByProduct = useMemo(() => {
    const totals = new Map();
    carts.forEach(c => {
      if (!c?.blastOutAt) return;
      const pid = Number(c.productId);
      const units = c.unitsInCart == null ? 0 : Number(c.unitsInCart);
      totals.set(pid, (totals.get(pid) || 0) + (Number.isFinite(units) ? units : 0));
    });
    return totals;
  }, [carts]);

  const blastOutTotalsList = useMemo(() => {
    const out = [];
    blastOutTotalsByProduct.forEach((units, pid) => {
      const name =
        productById.get(pid)?.name ||
        run?.Carts?.find(x => Number(x.productId) === pid)?.Product?.name ||
        `Product #${pid}`;
      out.push({ productId: pid, name, units });
    });
    out.sort((a, b) => b.units - a.units);
    return out;
  }, [blastOutTotalsByProduct, productById, run]);

  const totalUnitsBlastedOut = useMemo(() => {
    let sum = 0;
    blastOutTotalsByProduct.forEach(v => {
      const n = Number(v);
      if (Number.isFinite(n)) sum += n;
    });
    return sum;
  }, [blastOutTotalsByProduct]);

  // Carts blocking production completion
  const cartsNotYetFrozen = useMemo(() => {
    return carts.filter(c => !c?.blastInAt);
  }, [carts]);

  const cartsStillInFreezer = useMemo(() => {
    return carts.filter(c => c?.blastInAt && !c?.blastOutAt);
  }, [carts]);

  const hasBlockingCarts = cartsNotYetFrozen.length > 0 || cartsStillInFreezer.length > 0;

  // Derived from editable state — NO bare setState calls in render body
  const editedTotalUnits = useMemo(() => {
    return editableProductTotals.reduce((sum, r) => {
      const n = Number(r.units);
      return sum + (Number.isFinite(n) ? n : 0);
    }, 0);
  }, [editableProductTotals]);

  const editedTotalDiscarded = useMemo(() => {
    return editableProductTotals.reduce((sum, r) => {
      const n = Number(r.discarded);
      return sum + (Number.isFinite(n) ? n : 0);
    }, 0);
  }, [editableProductTotals]);

  const cartsWaitingBlastIn = useMemo(() => {
    if (mode !== 'packaging') return [];
    const nowMs = Date.now();
    return carts
      .filter(c => !c?.blastInAt)
      .map(c => {
        const createdAt = c?.createdAt ? new Date(c.createdAt) : null;
        const createdMs = createdAt ? createdAt.getTime() : NaN;
        const minsElapsed = Number.isFinite(createdMs) ? Math.floor((nowMs - createdMs) / 60000) : null;
        const minsRemaining = minsElapsed == null ? null : (Number(maxMinutesToFreezer) - minsElapsed);
        return { cart: c, createdAt, minsElapsed, minsRemaining };
      })
      .filter(x => x.createdAt && Number.isFinite(x.createdAt.getTime()))
      .sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, carts, maxMinutesToFreezer, tick]);

  const oldestWaitingBlastIn = cartsWaitingBlastIn[0] || null;

  const getServerMsg = (e, fallback) => e?.response?.data?.message || e?.message || fallback;

  const doPause = async () => {
    clearBanners();
    try {
      await bakingCcpService.pauseRun(runId);
      setSuccessMsg(t('Run paused.', 'Corrida pausada.', lang));
      fetchAll({ silent: true });
    } catch (e) {
      console.error(e);
      setError(getServerMsg(e, t('Failed to pause run.', 'Error al pausar la corrida.', lang)));
    }
  };

  const doResume = async () => {
    clearBanners();
    try {
      await bakingCcpService.resumeRun(runId);
      setSuccessMsg(t('Run resumed.', 'Corrida reanudada.', lang));
      fetchAll({ silent: true });
    } catch (e) {
      console.error(e);
      setError(getServerMsg(e, t('Failed to resume run.', 'Error al reanudar la corrida.', lang)));
    }
  };

  const doStopBaking = async (payload = {}) => {
    clearBanners();
    try {
      await bakingCcpService.stopBaking(runId, payload);
      setWarnMsg(t(L.stopStage1Notice.en, L.stopStage1Notice.es, lang));
      fetchAll({ silent: true });
      return true;
    } catch (e) {
      console.error(e);
      setError(getServerMsg(e, t('Failed to stop production.', 'Error al detener la producción.', lang)));
      return false;
    }
  };

  // Pasteurization requires a final temperature reading before stopping — open a small modal for
  // it instead of stopping immediately. Baking has no equivalent requirement.
  const handleStopClick = () => {
    if (isPasteurization) {
      clearBanners();
      setStopTempF('');
      setShowStopPasteurizeModal(true);
    } else {
      doStopBaking();
    }
  };

  const confirmStopPasteurizing = async () => {
    const n = Number(stopTempF);
    if (!Number.isFinite(n) || n <= 0) {
      setError(t('Please enter a valid final temperature in °F.', 'Ingrese una temperatura final válida en °F.', lang));
      return;
    }
    setSavingStop(true);
    try {
      const ok = await doStopBaking({ tempF: n });
      if (ok) setShowStopPasteurizeModal(false);
    } finally {
      setSavingStop(false);
    }
  };

  const doEndAging = async () => {
    clearBanners();
    try {
      await bakingCcpService.endAging(runId);
      setSuccessMsg(t('Aging ended. Churn batches can now be created.', 'Añejamiento terminado. Ya se pueden crear lotes de batido.', lang));
      fetchAll({ silent: true });
    } catch (e) {
      console.error(e);
      setError(getServerMsg(e, t('Failed to end aging.', 'Error al terminar el añejamiento.', lang)));
    }
  };

  const openTempModal = () => {
    clearBanners();
    setTempF('');
    const availableCarts = carts.filter(c => !c?.blastInAt);
    const lastCart = availableCarts[availableCarts.length - 1]; // carts sorted ascending, last = newest
    setTempCartId(lastCart ? String(lastCart.id) : '');
    setShowTempModal(true);
  };

  const saveTemp = async () => {
    clearBanners();
    if (!isBaking) {
      setError(t(`Temperature can only be recorded while run is in ${L.stage1Verb.en} status.`, `La temperatura solo puede registrarse mientras la corrida está en estado ${L.stage1Verb.es}.`, lang));
      return;
    }
    const n = Number(tempF);
    if (!Number.isFinite(n) || n <= 0) {
      setError(t('Please enter a valid temperature in °F.', 'Ingrese una temperatura válida en °F.', lang));
      return;
    }
    setSavingTemp(true);
    try {
      await bakingCcpService.addTempReading(runId, {
        temperatureF: n,
        cartId: tempCartId ? Number(tempCartId) : null,
      });
      setSuccessMsg(t('Temperature recorded.', 'Temperatura registrada.', lang));
      setShowTempModal(false);
      fetchAll({ silent: true });
    } catch (e) {
      console.error(e);
      setError(getServerMsg(e, t('Failed to record temperature.', 'Error al registrar la temperatura.', lang)));
    } finally {
      setSavingTemp(false);
    }
  };

  const openCartModal = () => {
    clearBanners();
    // Default to the last cart's product (most recently used), fall back to first scheduled product
    const lastCart = carts.length > 0 ? carts[carts.length - 1] : null;
    const defaultPid = lastCart
      ? String(lastCart.productId)
      : (scheduledProducts[0] ? String(scheduledProducts[0].id) : (run?.productId ? String(run.productId) : ''));
    setCartProductId(defaultPid);
    const p = defaultPid ? productById.get(Number(defaultPid)) : null;
    setCartUnits(p?.defaultUnitsPerCart != null ? String(p.defaultUnitsPerCart) : '');
    setCartNotes('');
    setShowCartModal(true);
  };

  useEffect(() => {
    if (!showCartModal) return;
    if (!cartProductId) return;
    const p = productById.get(Number(cartProductId));
    if (!p) return;
    if (cartUnits === '' || cartUnits == null) {
      if (p.defaultUnitsPerCart != null) setCartUnits(String(p.defaultUnitsPerCart));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartProductId, showCartModal, productById]);

  const openEditCart = (cart) => {
    setEditingCartId(cart.id);
    setCartProductId(String(cart.productId));
    setCartUnits(cart.unitsInCart != null ? String(cart.unitsInCart) : '');
    setCartNotes(cart.notes || '');
    setShowCartModal(true);
  };

  // datetime-local inputs need local "YYYY-MM-DDTHH:mm" strings, not ISO
  const toDatetimeLocal = (val) => {
    if (!val) return '';
    const d = new Date(val);
    if (Number.isNaN(d.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const openEditTimes = (cart) => {
    clearBanners();
    setEditTimesCart(cart);
    setEditTimesForm({
      ovenOutAt: toDatetimeLocal(cart.ovenOutAt),
      blastInAt: toDatetimeLocal(cart.blastInAt),
      blastOutAt: toDatetimeLocal(cart.blastOutAt),
    });
    setEditTimesNote('');
  };

  const saveEditTimes = async () => {
    if (!editTimesCart) return;
    if (!editTimesNote.trim()) {
      setError(t('A reason is required when editing times.', 'Se requiere un motivo para editar los horarios.', lang));
      return;
    }
    setSavingEditTimes(true);
    try {
      await bakingCcpService.updateCartTimes(editTimesCart.id, {
        ovenOutAt: editTimesForm.ovenOutAt ? new Date(editTimesForm.ovenOutAt).toISOString() : null,
        blastInAt: editTimesForm.blastInAt ? new Date(editTimesForm.blastInAt).toISOString() : null,
        blastOutAt: editTimesForm.blastOutAt ? new Date(editTimesForm.blastOutAt).toISOString() : null,
        adjustNote: editTimesNote.trim(),
      });
      setSuccessMsg(t('Times updated.', 'Horarios actualizados.', lang));
      setEditTimesCart(null);
      fetchAll({ silent: true });
    } catch (e) {
      console.error(e);
      setError(getServerMsg(e, t('Failed to update times.', 'Error al actualizar los horarios.', lang)));
    } finally {
      setSavingEditTimes(false);
    }
  };

  const createCart = async () => {
    clearBanners();
    if (!cartProductId) {
      setError(t(`Select a product for the ${L.unit.en.toLowerCase()}.`, `Selecciona un producto para el ${L.unit.es.toLowerCase()}.`, lang));
      return;
    }
    const units = cartUnits === '' ? null : Number(cartUnits);
    if (units != null && (!Number.isFinite(units) || units <= 0)) {
      setError(t(`Units in ${L.unit.en.toLowerCase()} must be a positive number.`, `Las unidades en el ${L.unit.es.toLowerCase()} deben ser un número positivo.`, lang));
      return;
    }
    setSavingCart(true);
    try {
      if (editingCartId) {
        await bakingCcpService.updateCart(editingCartId, {
          productId: Number(cartProductId),
          unitsInCart: units,
          notes: cartNotes || null,
        });
        setSuccessMsg(t(`${L.unit.en} updated.`, `${L.unit.es} actualizado.`, lang));
      } else {
        if (!(isPasteurization ? isReadyToChurn : (isBaking || isPaused))) {
          setError(t(`Cannot create ${L.unitPlural.en.toLowerCase()} in the current run status.`, `No se pueden crear ${L.unitPlural.es.toLowerCase()} en el estado actual de la corrida.`, lang));
          setSavingCart(false);
          return;
        }
        await bakingCcpService.createCart(runId, {
          productId: Number(cartProductId),
          unitsInCart: units,
          notes: cartNotes || null,
        });
        setSuccessMsg(t(`${L.unit.en} created.`, `${L.unit.es} creado.`, lang));
      }
      setShowCartModal(false);
      setEditingCartId(null);
      fetchAll({ silent: true });
    } catch (e) {
      console.error(e);
      setError(getServerMsg(e, editingCartId
        ? t(`Failed to update ${L.unit.en.toLowerCase()}.`, `Error al actualizar el ${L.unit.es.toLowerCase()}.`, lang)
        : t(`Failed to create ${L.unit.en.toLowerCase()}.`, `Error al crear el ${L.unit.es.toLowerCase()}.`, lang)));
    } finally {
      setSavingCart(false);
    }
  };

  const blastIn = async (cartId) => {
    clearBanners();
    try {
      await bakingCcpService.markCartBlastIn(cartId);
      setSuccessMsg(t(`${L.unit.en} marked: ${L.blastInLabel.en}.`, `${L.unit.es} marcado: ${L.blastInLabel.es}.`, lang));
      fetchAll({ silent: true });
    } catch (e) {
      console.error(e);
      setError(getServerMsg(e, t(`Failed to mark ${L.blastInLabel.en}.`, `Error al marcar ${L.blastInLabel.es}.`, lang)));
    }
  };

  const blastOut = async (cartId) => {
    clearBanners();
    try {
      await bakingCcpService.markCartBlastOut(cartId);
      setSuccessMsg(t(`${L.unit.en} marked: ${L.blastOutLabel.en}.`, `${L.unit.es} marcado: ${L.blastOutLabel.es}.`, lang));
      fetchAll({ silent: true });
    } catch (e) {
      console.error(e);
      setError(getServerMsg(e, t(`Failed to mark ${L.blastOutLabel.en}.`, `Error al marcar ${L.blastOutLabel.es}.`, lang)));
    }
  };

  const questions = useMemo(() => {
    const q = run?.packagingQuestionsJson ?? cfg?.packagingQuestionsJson ?? [];
    return Array.isArray(q) ? q : [];
  }, [run, cfg]);

  const normalizedQuestions = useMemo(() => {
    return questions.map((q, idx) => {
      if (typeof q === 'string') {
        return { key: `q_${idx}`, question: q, type: 'CHECK', required: true };
      }
      const key = q.key || q.id || `q_${idx}`;
      return {
        key,
        question: q.question || q.label || `Question ${idx + 1}`,
        type: (q.type || 'CHECK').toUpperCase(),
        required: q.required !== false,
        options: Array.isArray(q.options) ? q.options : [],
      };
    });
  }, [questions]);

  const openCompleteModal = () => {
    clearBanners();

    // Block if any carts haven't completed the freezer cycle
    if (hasBlockingCarts) {
      setShowBlockingModal(true);
      return;
    }

    const fgLot = run?.Batch?.lotCode || '';
    setFinLot(fgLot);
    setQtyProduced(totalUnitsBlastedOut ? String(totalUnitsBlastedOut) : '');
    setPackNotes(run?.packagingNotes || '');
    setDeviation(!!run?.deviation);
    setDeviationNotes(run?.deviationNotes || '');
    setCorrectiveNotes(run?.correctiveActionNotes || '');

    const seed = {};
    const prev = Array.isArray(run?.packagingAnswersJson) ? run.packagingAnswersJson : [];
    prev.forEach(a => {
      if (!a) return;
      const k = a.key || a.questionKey || a.question || null;
      if (k) seed[k] = a.value ?? a.checked ?? a.answer ?? null;
    });
    setAnswers(seed);

    // ✅ Seed editable totals here — the ONLY place this should be set
    setEditableProductTotals(blastOutTotalsList.map(r => ({ ...r, discarded: r.discarded ?? 0 })));

    setShowCompleteModal(true);
  };

  const allRequiredAnswered = useMemo(() => {
    return normalizedQuestions.every(q => {
      if (!q.required) return true;
      const v = answers[q.key];
      if (q.type === 'CHECK') return v === true;
      if (q.type === 'TEXT') return String(v || '').trim().length > 0;
      if (q.type === 'NUMBER') return Number.isFinite(Number(v));
      if (q.type === 'SELECT') return String(v || '').trim().length > 0;
      return v != null && String(v).trim() !== '';
    });
  }, [normalizedQuestions, answers]);

  const doComplete = async () => {
    clearBanners();

    if (isVerified) {
      setError(t('Run is verified and cannot be changed.', 'La corrida está verificada y no puede modificarse.', lang));
      return;
    }
    if (!isStopped) {
      setError(isPasteurization
        ? t('Packaging completion requires the batch to be Ready to Churn (aging ended) first.', 'Completar el empaque requiere que el lote esté Listo para Batir (añejamiento terminado) primero.', lang)
        : t('Packaging completion requires baking to be STOPPED first.', 'Completar el empaque requiere que el horneado esté DETENIDO primero.', lang));
      return;
    }
    if (hasBlockingCarts) {
      setError(t(`All ${L.unitPlural.en.toLowerCase()} must complete the ${L.cycleLabel.en} cycle before closing the run.`, `Todos los ${L.unitPlural.es.toLowerCase()} deben completar el ciclo de ${L.cycleLabel.es} antes de cerrar la corrida.`, lang));
      return;
    }
    if (!String(finLot || '').trim()) {
      setError(t('Finished Goods Lot Code is required.', 'El Código de Lote de Producto Terminado es obligatorio.', lang));
      return;
    }
    if (!allRequiredAnswered) {
      setError(t('Please answer all required packaging checklist questions.', 'Por favor responde todas las preguntas requeridas de la lista de empaque.', lang));
      return;
    }

    setSavingComplete(true);
    try {
      const packagingAnswersJson = normalizedQuestions.map(q => ({
        key: q.key,
        question: q.question,
        type: q.type,
        required: q.required,
        value: answers[q.key] ?? null,
        at: new Date().toISOString(),
      }));

      await bakingCcpService.completeRun(runId, {
        packagingAnswersJson,
        finishedGoodsLotCode: String(finLot).trim(),
        quantityProduced: editedTotalUnits,
        productTotals: editableProductTotals.map(r => ({
          productId: r.productId,
          name: r.name,
          units: Number(r.units) || 0,
          discarded: Number(r.discarded) || 0,
        })),
        notes: packNotes || '',
        deviation: !!deviation,
        deviationNotes: deviationNotes || '',
        correctiveActionNotes: correctiveNotes || '',
      });

      setSuccessMsg(t('Production completed and sent to QA for verification.', 'Producción completada y enviada a QA para verificación.', lang));
      setShowCompleteModal(false);
      // Create today's after-production instance now that production is complete
      sqfService.prepareAfterProductionGate().catch(() => ({ gated: false, pending: [] })).then(setAfterGateStatus);
      fetchAll({ silent: true });
          // Tell the navbar to refresh — run is no longer active
      window.dispatchEvent(new Event('productionStatusChanged'));
    } catch (e) {
      console.error(e);
      setError(getServerMsg(e, t('Failed to complete production.', 'Error al completar la producción.', lang)));
    } finally {
      setSavingComplete(false);
    }
  };

  useEffect(() => {
    if (!successMsg && !warnMsg) return;
    const t = setTimeout(() => {
      setSuccessMsg('');
      setWarnMsg('');
    }, 3500);
    return () => clearTimeout(t);
  }, [successMsg, warnMsg]);

  const tempQuick = L.vesselTempQuick;
  const unitsQuick = [30, 40, 60, 80];
  const disableBakingControlsInPackaging = mode === 'packaging';

  return (
    <>
    <style>{`
      @keyframes borderFlash {
        0%, 100% { box-shadow: 0 0 0 2px transparent; }
        50% { box-shadow: 0 0 0 3px #fd7e14; }
      }
    `}</style>
    <div className="card">
      <div className="card-body">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
          <div>
            <h4 className="mb-0">{isNoBake ? t('Production — Live Mode', 'Producción — Modo en Vivo', lang) : t(L.pageTitleLive.en, L.pageTitleLive.es, lang)}</h4>
            <div className="text-muted" style={{ fontSize: 12 }}>
              {isNoBake
                ? t('No-bake run. Two modes: Production and Packaging.', 'Corrida sin horneado. Dos modos: Producción y Empaque.', lang)
                : t(`One run. Two modes: ${L.stage1Action.en} and Packaging.`, `Una corrida. Dos modos: ${L.stage1Action.es} y Empaque.`, lang)}
            </div>
          </div>

          <div className="d-flex gap-2">
            <Link className="btn btn-outline-secondary" to="/ccp/baking/start">
              {t('Start Page', 'Página de Inicio', lang)}
            </Link>
            <Link className="btn btn-outline-secondary" to="/ccp/baking/config">
              {t('Config', 'Configurar', lang)}
            </Link>
            <button className="btn btn-outline-primary" onClick={() => fetchAll()} disabled={loading}>
              {loading ? t('Loading…', 'Cargando…', lang) : t('Refresh', 'Actualizar', lang)}
            </button>
          </div>
        </div>

        {/* SQF pre-operation gate */}
        {gateStatus.gated && (() => {
          const item = gateStatus.pending[0];
          const beingFilled = item && isLockFresh(item.fillingStartedAt) && item.fillingByUserName;
          const hasInstance = !!item?.instanceId;
          return (
            <div className={`alert ${beingFilled ? 'alert-info' : 'alert-danger'} d-flex align-items-center gap-3 mt-3 mb-0 py-2`}>
              <span style={{ fontSize: 20 }}>{beingFilled ? '📋' : '⛔'}</span>
              <div className="flex-fill">
                <strong>
                  {beingFilled
                    ? t('Checklist in progress', 'Lista en progreso', lang)
                    : t('Pre-operation checklist not completed', 'Lista de verificación pre-operación incompleta', lang)}
                </strong>
                <div style={{ fontSize: 12 }}>
                  {beingFilled
                    ? t(`${item.fillingByUserName} is already completing this checklist.`, `${item.fillingByUserName} ya está completando esta lista.`, lang)
                    : `${gateStatus.pending.map(p => p.templateName).join(', ')} ${t('must be completed before production starts.', 'debe completarse antes de que comience la producción.', lang)}`}
                </div>
              </div>
              <button
                className={`btn ${beingFilled ? 'btn-outline-primary' : 'btn-danger'} btn-sm ms-auto text-nowrap`}
                disabled={preparingGate}
                onClick={() => handleGateBannerClick(gateStatus.pending, setGateStatus)}
              >
                {preparingGate ? '…' : beingFilled
                  ? t('Open Anyway →', 'Abrir de todas formas →', lang)
                  : hasInstance
                    ? t('Resume →', 'Continuar →', lang)
                    : t('Start →', 'Iniciar →', lang)}
              </button>
            </div>
          );
        })()}

        {/* SQF end-of-production gate */}
        {afterGateStatus.gated && (() => {
          const item = afterGateStatus.pending[0];
          const beingFilled = item && isLockFresh(item.fillingStartedAt) && item.fillingByUserName;
          const hasInstance = !!item?.instanceId;
          return (
            <div className={`alert ${beingFilled ? 'alert-info' : 'alert-warning'} d-flex align-items-center gap-3 mt-3 mb-0 py-2`}>
              <span style={{ fontSize: 20 }}>{beingFilled ? '📋' : '🧹'}</span>
              <div className="flex-fill">
                <strong>
                  {beingFilled
                    ? t('Checklist in progress', 'Lista en progreso', lang)
                    : t('End-of-production checklist pending', 'Lista de verificación de fin de producción pendiente', lang)}
                </strong>
                <div style={{ fontSize: 12 }}>
                  {beingFilled
                    ? t(`${item.fillingByUserName} is already completing this checklist.`, `${item.fillingByUserName} ya está completando esta lista.`, lang)
                    : `${afterGateStatus.pending.map(p => p.templateName).join(', ')} ${t('must be completed before closing the shift.', 'debe completarse antes de cerrar el turno.', lang)}`}
                </div>
              </div>
              <button
                className={`btn ${beingFilled ? 'btn-outline-primary' : 'btn-warning'} btn-sm ms-auto text-nowrap`}
                disabled={preparingGate}
                onClick={() => handleGateBannerClick(afterGateStatus.pending, setAfterGateStatus)}
              >
                {preparingGate ? '…' : beingFilled
                  ? t('Open Anyway →', 'Abrir de todas formas →', lang)
                  : hasInstance
                    ? t('Resume →', 'Continuar →', lang)
                    : t('Start →', 'Iniciar →', lang)}
              </button>
            </div>
          );
        })()}

        {/* Mode toggle */}
        <div className="mt-3 d-flex gap-2">
          <button
            className={`btn btn-lg flex-fill ${mode === 'baking' ? 'btn-primary' : 'btn-outline-primary'}`}
            style={{ fontWeight: 900 }}
            onClick={() => setModeAndUrl('baking')}
          >
            {isNoBake ? t('PRODUCTION', 'PRODUCCIÓN', lang) : t(L.stage1Verb.en, L.stage1Verb.es, lang)}
          </button>
          <button
            className={`btn btn-lg flex-fill ${mode === 'packaging' ? 'btn-primary' : 'btn-outline-primary'}`}
            style={{ fontWeight: 900 }}
            onClick={() => setModeAndUrl('packaging')}
          >
            {t('PACKAGING', 'EMPAQUE', lang)}
          </button>
        </div>

        {/* Feedback */}
        {error && <div className="alert alert-danger mt-3 mb-0">{error}</div>}
        {warnMsg && <div className="alert alert-warning mt-3 mb-0">{warnMsg}</div>}
        {successMsg && <div className="alert alert-success mt-3 mb-0">{successMsg}</div>}

        {run && (
          <>
            <div className="row mt-3 g-3">
              {/* Left card */}
              <div className="col-12 col-lg-6">
                <div className="card">
                  <div className="card-body">
                    <div className="text-muted">{t('Batch / Lot Code', 'Lote / Código de Lote', lang)}</div>
                    <div style={{ fontSize: 26, fontWeight: 900 }}>{runLot}</div>
                    <div className="text-muted" style={{ fontSize: 12 }}>
                      {t('Production date:', 'Fecha de producción:', lang)} {runProdDate}
                    </div>
                    <div className="text-muted" style={{ fontSize: 12 }}>
                      {t('Status:', 'Estado:', lang)} <span style={{ fontWeight: 800 }}>{statusLabel || '—'}</span>
                    </div>
                    <div className="text-muted" style={{ fontSize: 12 }}>
                      {t('Product (primary):', 'Producto (principal):', lang)} {run?.Product?.name || '—'}
                    </div>

                    <div className="mt-3 d-flex flex-wrap gap-2">
                      {!isNoBake && (
                        <>
                          <button
                            className="btn btn-outline-secondary"
                            onClick={doPause}
                            disabled={disableBakingControlsInPackaging || !canPause}
                            title={disableBakingControlsInPackaging ? t(`${L.stage1Action.en} controls are disabled in Packaging mode.`, `Los controles de ${L.stage1Action.es.toLowerCase()} están desactivados en modo Empaque.`, lang) : ''}
                          >
                            {t('Pause', 'Pausar', lang)}
                          </button>
                          <button
                            className="btn btn-outline-secondary"
                            onClick={doResume}
                            disabled={disableBakingControlsInPackaging || !canResume}
                            title={disableBakingControlsInPackaging ? t(`${L.stage1Action.en} controls are disabled in Packaging mode.`, `Los controles de ${L.stage1Action.es.toLowerCase()} están desactivados en modo Empaque.`, lang) : ''}
                          >
                            {t('Resume', 'Reanudar', lang)}
                          </button>
                        </>
                      )}
                      <button
                        className="btn btn-outline-danger"
                        onClick={handleStopClick}
                        disabled={disableBakingControlsInPackaging || !canStopBaking}
                        title={disableBakingControlsInPackaging
                          ? t(`${L.stage1Action.en} controls are disabled in Packaging mode.`, `Los controles de ${L.stage1Action.es.toLowerCase()} están desactivados en modo Empaque.`, lang)
                          : (isPasteurization && !holdTimeMet
                            ? t('Minimum hold time has not been reached yet.', 'El tiempo mínimo de sostenimiento aún no se ha alcanzado.', lang)
                            : '')}
                      >
                        {isNoBake ? t('Stop Producing', 'Detener Producción', lang) : t(L.stopStage1Button.en, L.stopStage1Button.es, lang)}
                        {isPasteurization && isBaking && !holdTimeMet && ' ⚠'}
                      </button>
                    </div>

                    <div className="text-muted mt-2" style={{ fontSize: 12 }}>
                      {isNoBake
                        ? t(`"Stop Producing" closes production; packaging (${L.machineLabel.en} + checklist) continues.`, `"Detener Producción" cierra la producción; el empaque (${L.machineLabel.es} + lista) continúa.`, lang)
                        : t(`"${L.stopStage1Button.en}" ends CCP1; packaging (${L.machineLabel.en} + checklist) continues.`, `"${L.stopStage1Button.es}" finaliza CCP1; el empaque (${L.machineLabel.es} + lista) continúa.`, lang)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Right card */}
              {mode === 'baking' ? (
                <div className="col-12 col-lg-6">
                  <div
                    className="card"
                    style={isApproaching || isOverdue ? {
                      animation: 'borderFlash 1.2s ease-in-out infinite',
                    } : {}}
                  >
                    <div className="card-body">
                      <div className="text-muted" style={{ fontSize: 12 }}>
                        {t('Temperature interval:', 'Intervalo de temperatura:', lang)} {maxMinutes} {t('min between readings', 'min entre lecturas', lang)}
                      </div>
                      <div className="text-muted mt-1" style={{ fontSize: 11 }}>
                        {t(L.intervalCheckNote.en, L.intervalCheckNote.es, lang)}
                      </div>

                      {!isBaking ? (
                        <div className="mt-2 text-muted" style={{ fontSize: 14 }}>
                          {t(`Temperature monitoring is active only while ${L.stage1Gerund.en}.`, `El monitoreo de temperatura está activo solo mientras se está ${L.stage1Gerund.es}.`, lang)}
                        </div>
                      ) : (
                        <>
                          <div className="mt-2 d-flex align-items-baseline gap-2">
                            <div style={{ fontSize: 13, color: '#6c757d' }}>{t('Next reading in', 'Próxima lectura en', lang)}</div>
                          </div>
                          <div style={{
                            fontSize: 48,
                            fontWeight: 900,
                            lineHeight: 1,
                            color: isOverdue ? '#dc3545' : isApproaching ? '#fd7e14' : '#212529'
                          }}>
                            {isOverdue
                              ? `+${Math.abs(minsUntilNextTemp)} min`
                              : minsUntilNextTemp != null
                                ? `${minsUntilNextTemp} min`
                                : '—'}
                          </div>
                          <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                            {t('Last reading:', 'Última lectura:', lang)} {lastTempAt ? lastTempAt.toLocaleString() : t('No readings yet', 'Sin lecturas aún', lang)}
                          </div>
                          {isPasteurization && requiredHoldMinutes != null && (
                            <div className="mt-3 pt-3" style={{ borderTop: '1px solid #eee' }}>
                              <div className="text-muted" style={{ fontSize: 12 }}>
                                {t('Hold time required:', 'Tiempo de sostenimiento requerido:', lang)} {requiredHoldMinutes} {t('min', 'min', lang)}
                              </div>
                              <div style={{ fontSize: 20, fontWeight: 900, color: holdTimeMet ? '#198754' : '#dc3545' }}>
                                {holdMinutesElapsed != null ? `${holdMinutesElapsed} / ${requiredHoldMinutes} min` : '—'}
                              </div>
                              {!holdTimeMet && (
                                <div className="text-danger mt-1" style={{ fontSize: 12, fontWeight: 700 }}>
                                  {t('Minimum hold time not yet met — do not stop pasteurizing yet.', 'Tiempo mínimo de sostenimiento no alcanzado aún — no detenga la pasteurización todavía.', lang)}
                                </div>
                              )}
                            </div>
                          )}
                          {isOverdue && (
                            <div className="alert alert-danger mt-3 mb-0" style={{ fontWeight: 800 }}>
                              {t('TEMP OVERDUE — record a temperature now.', 'TEMPERATURA VENCIDA — registra una temperatura ahora.', lang)}
                            </div>
                          )}
                          {isApproaching && !isOverdue && (
                            <div className="alert alert-warning mt-3 mb-0" style={{ fontWeight: 800 }}>
                              {t('Next temperature reading due soon.', 'Próxima lectura de temperatura próxima.', lang)}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="col-12 col-lg-6">
                  <div className="card">
                    <div className="card-body">
                      <div className="text-muted">{t('Cold Chain Control', 'Control de Cadena de Frío', lang)}</div>
                      <div style={{ fontSize: 22, fontWeight: 800 }}>
                        {t('Remaining allowed time above 41°F', 'Tiempo permitido restante por encima de 41°F', lang)}
                      </div>
                      <div className="text-muted" style={{ fontSize: 12 }}>
                        {t(`Clock starts when the ${L.unit.en.toLowerCase()} is created. Must be marked "${L.blastInLabel.en}" within`, `El reloj comienza cuando se crea el ${L.unit.es.toLowerCase()}. Debe marcarse "${L.blastInLabel.es}" en`, lang)} {maxMinutesToFreezer} {t('minutes.', 'minutos.', lang)}
                      </div>

                      {!oldestWaitingBlastIn ? (
                        <div className="mt-3 text-muted" style={{ fontSize: 14 }}>
                          {t(`No ${L.unitPlural.en.toLowerCase()} currently waiting for ${L.blastInLabel.en}.`, `No hay ${L.unitPlural.es.toLowerCase()} esperando ${L.blastInLabel.es}.`, lang)}
                        </div>
                      ) : (
                        <div className="mt-3">
                          <div className="text-muted" style={{ fontSize: 12 }}>
                            {t(`Oldest ${L.unit.en.toLowerCase()} waiting for ${L.blastInLabel.en}`, `${L.unit.es} más antiguo esperando ${L.blastInLabel.es}`, lang)}
                          </div>
                          <div className="d-flex align-items-center justify-content-between">
                            <div style={{ fontSize: 22, fontWeight: 900 }}>
                              {L.unit.en} #{oldestWaitingBlastIn.cart.cartNumber}
                            </div>
                            <div style={{ fontSize: 22, fontWeight: 900 }}>
                              {oldestWaitingBlastIn.minsRemaining == null
                                ? '—'
                                : `${oldestWaitingBlastIn.minsRemaining} min`}
                            </div>
                          </div>
                          <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                            {t('Created:', 'Creado:', lang)} {oldestWaitingBlastIn.createdAt ? oldestWaitingBlastIn.createdAt.toLocaleString() : '—'}
                          </div>
                          {oldestWaitingBlastIn.minsRemaining != null && oldestWaitingBlastIn.minsRemaining < 0 && (
                            <div className="alert alert-danger mt-3 mb-0" style={{ fontWeight: 800 }}>
                              {t(`OVERDUE — This ${L.unit.en.toLowerCase()} exceeded the allowed time above 41°F.`, `VENCIDO — Este ${L.unit.es.toLowerCase()} excedió el tiempo permitido por encima de 41°F.`, lang)}
                            </div>
                          )}
                          {cartsWaitingBlastIn.length > 1 && (
                            <div className="mt-3">
                              <div className="text-muted" style={{ fontSize: 12 }}>{t(`Next ${L.unitPlural.en.toLowerCase()}:`, `Próximos ${L.unitPlural.es.toLowerCase()}:`, lang)}</div>
                              <ul className="mb-0" style={{ fontSize: 13 }}>
                                {cartsWaitingBlastIn.slice(1, 4).map(x => (
                                  <li key={x.cart.id}>
                                    {L.unit.en} #{x.cart.cartNumber} — {x.minsRemaining == null ? '—' : `${x.minsRemaining} min`}
                                  </li>
                                ))}
                                {cartsWaitingBlastIn.length > 4 && (
                                  <li>…{t('and', 'y', lang)} {cartsWaitingBlastIn.length - 4} {t('more', 'más', lang)}</li>
                                )}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Aging hold — pasteurization flow only, shown while the base rests before churning */}
              {mode === 'baking' && isPasteurization && isAging && (
                <div className="col-12">
                  <div className="card border-warning">
                    <div className="card-body">
                      <div style={{ fontWeight: 900, fontSize: 16 }}>{t(L.agingCardTitle.en, L.agingCardTitle.es, lang)}</div>
                      <div className="text-muted mt-1" style={{ fontSize: 13 }}>
                        {t(L.agingInProgress.en, L.agingInProgress.es, lang)}
                      </div>
                      {run?.agingStartedAt && (
                        <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                          {t('Aging started:', 'Añejamiento iniciado:', lang)} {new Date(run.agingStartedAt).toLocaleString()}
                        </div>
                      )}
                      {run?.pasteurizationTempEndF != null && (
                        <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                          {t('Final temperature at stop:', 'Temperatura final al detener:', lang)} <strong>{Number(run.pasteurizationTempEndF).toFixed(1)}°F</strong>
                        </div>
                      )}
                      {agingHoursElapsed != null && (minAgingHours != null || maxAgingHours != null) && (
                        <div className="mt-2">
                          <div style={{
                            fontSize: 18, fontWeight: 900,
                            color: agingOverMax ? '#dc3545' : agingUnderMin ? '#fd7e14' : '#212529',
                          }}>
                            {agingHoursElapsed.toFixed(1)} {t('hours elapsed', 'horas transcurridas', lang)}
                            {minAgingHours != null && maxAgingHours != null && ` (${minAgingHours}–${maxAgingHours}h ${t('target', 'objetivo', lang)})`}
                          </div>
                          {agingUnderMin && (
                            <div className="text-warning" style={{ fontSize: 12, fontWeight: 700 }}>
                              {t('Below the minimum aging time configured.', 'Por debajo del tiempo mínimo de añejamiento configurado.', lang)}
                            </div>
                          )}
                          {agingOverMax && (
                            <div className="text-danger" style={{ fontSize: 12, fontWeight: 700 }}>
                              {t('OVERDUE — aging has exceeded the configured maximum.', 'VENCIDO — el añejamiento superó el máximo configurado.', lang)}
                            </div>
                          )}
                        </div>
                      )}
                      {agingUnderMin && (
                        <div className="alert alert-warning mt-2 mb-0 py-2" style={{ fontSize: 12 }}>
                          {t('Ending aging now is below the configured minimum — double-check before proceeding.', 'Terminar el añejamiento ahora está por debajo del mínimo configurado — verifique antes de continuar.', lang)}
                        </div>
                      )}
                      <button
                        className="btn btn-warning btn-lg w-100 mt-3"
                        style={{ fontSize: 20, fontWeight: 900, padding: '14px 16px' }}
                        onClick={doEndAging}
                        disabled={!canEndAging}
                      >
                        {t(L.endAgingButton.en, L.endAgingButton.es, lang)}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Primary actions */}
              {mode === 'baking' && (
                <div className="col-12">
                  <div className="d-flex flex-column flex-md-row gap-2">
                    {!isNoBake && (
                      <button
                        className="btn btn-primary btn-lg flex-fill"
                        style={{ fontSize: 22, fontWeight: 900, padding: '14px 16px' }}
                        onClick={openTempModal}
                        disabled={!canRecordTemp}
                        title={!canRecordTemp ? t(`Temp can be recorded only in ${L.stage1Verb.en} status.`, `La temperatura solo puede registrarse en estado ${L.stage1Verb.es}.`, lang) : ''}
                      >
                        {t('RECORD TEMPERATURE', 'REGISTRAR TEMPERATURA', lang)}
                      </button>
                    )}
                    <button
                      className="btn btn-outline-secondary btn-lg flex-fill"
                      style={{ fontSize: 22, fontWeight: 900, padding: '14px 16px' }}
                      onClick={openCartModal}
                      disabled={!canCreateCart}
                      title={!canCreateCart
                        ? (isPasteurization
                          ? t('Batches can be created only once aging ends (Ready to Churn).', 'Los lotes solo pueden crearse cuando termina el añejamiento (Listo para Batir).', lang)
                          : t('Carts can be created only while BAKING or PAUSED.', 'Los carritos solo pueden crearse mientras se está HORNEANDO o PAUSADO.', lang))
                        : ''}
                    >
                      {t(L.newUnitButton.en, L.newUnitButton.es, lang)}
                    </button>
                  </div>
                  <div className="text-muted mt-2" style={{ fontSize: 12 }}>
                    {isPasteurization
                      ? t('Tip: batches unlock once aging ends; packaging uses Start/Stop Churning below.', 'Consejo: los lotes se habilitan cuando termina el añejamiento; empaque usa Iniciar/Detener Batido abajo.', lang)
                      : isNoBake
                        ? t('Tip: tap NEW CART to add production carts; packaging uses Blast In/Out below.', 'Consejo: toca NUEVO CARRITO para agregar carritos; empaque usa Entrada/Salida de Congelador abajo.', lang)
                        : t('Tip: baking taps NEW CART; packaging uses Blast In/Out below.', 'Consejo: horneado toca NUEVO CARRITO; empaque usa Entrada/Salida de Congelador abajo.', lang)}
                  </div>
                </div>
              )}

              {/* Visual Inspection launcher — mid-production, while packaging is actually happening
                  (not on the QA Verify page, which only exists once the run is already complete) */}
              {mode === 'packaging' && (
                <div className="col-12">
                  <button className="btn btn-outline-primary btn-sm" onClick={() => setShowVisualInspectionModal(true)}>
                    📷 {t('Visual Inspection', 'Inspección Visual', lang)}
                  </button>
                  <div className="mt-2">
                    <VisualInspectionsList inspections={visualInspections} />
                  </div>
                </div>
              )}

              {/* Packaging action */}
              {mode === 'packaging' && (
                <div className="col-12">
                  <button
                    className="btn btn-success btn-lg w-100"
                    style={{ fontSize: 22, fontWeight: 900, padding: '14px 16px' }}
                    onClick={openCompleteModal}
                    disabled={!isStopped || isVerified || isCompleted}
                    title={!isStopped ? (isNoBake ? t('Stop Producing first, then complete production.', 'Detén la producción primero, luego completa.', lang) : t(`${L.stopStage1Button.en} first, then complete production.`, `${L.stopStage1Button.es} primero, luego completa la producción.`, lang)) : ''}
                  >
                    {t('COMPLETE PRODUCTION (CLOSE RUN)', 'COMPLETAR PRODUCCIÓN (CERRAR CORRIDA)', lang)}
                  </button>
                  <div className="text-muted mt-2" style={{ fontSize: 12 }}>
                    {t('Complete production and send to QA for verification and validation.', 'Completar producción y enviar a QA para verificación y validación.', lang)}
                  </div>
                  {!isStopped && (
                    <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                      {t('Requirement: run status must be', 'Requisito: el estado de la corrida debe ser', lang)} <b>{isNoBake ? t('PRODUCTION STOPPED', 'PRODUCCIÓN DETENIDA', lang) : L.requirementStatus.en}</b>.
                    </div>
                  )}
                </div>
              )}

              {/* Freezer window alerts */}
              {cartsOverdueFreezer.length > 0 && (
                <div className="col-12">
                  <div className="alert alert-danger mb-0" style={{ fontWeight: 800 }}>
                    {t(`${L.machineLabel.en.toUpperCase()} WINDOW OVERDUE —`, `VENTANA DE ${L.machineLabel.es.toUpperCase()} VENCIDA —`, lang)} {cartsOverdueFreezer.length} {t(`${L.unit.en.toLowerCase()}(s) have been out of the ${L.vessel.en.toLowerCase()} more than`, `${L.unit.es.toLowerCase()}(s) ha(n) estado fuera de ${L.vessel.es.toLowerCase()} más de`, lang)} {maxMinutesToFreezer} {t(`minutes without ${L.blastOutLabel.en}.`, `minutos sin ${L.blastOutLabel.es}.`, lang)}
                  </div>
                </div>
              )}

              {/* Packaging dashboard */}
              {mode === 'packaging' && (
                <>
                  <div className="col-12 col-lg-6">
                    <div className="card">
                      <div className="card-body">
                        <div style={{ fontWeight: 900, fontSize: 16 }}>{t(`In ${L.machineLabel.en} (${L.blastInLabel.en}, not ${L.blastOutLabel.en})`, `En ${L.machineLabel.es} (${L.blastInLabel.es}, sin ${L.blastOutLabel.es})`, lang)}</div>
                        <div className="text-muted" style={{ fontSize: 12 }}>
                          {t(`Total ${L.unitPlural.en.toLowerCase()} in ${L.machineLabel.en}:`, `Total de ${L.unitPlural.es.toLowerCase()} en ${L.machineLabel.es}:`, lang)} {freezerCarts.length}
                        </div>
                        <div className="table-responsive mt-3">
                          <table className="table table-sm align-middle">
                            <thead>
                              <tr>
                                <th style={{ width: 80 }}>{t(L.unit.en, L.unit.es, lang)}</th>
                                <th>{t('Product', 'Producto', lang)}</th>
                                <th style={{ width: 90 }}>{t('Units', 'Unidades', lang)}</th>
                                <th style={{ width: 120 }}>{t(L.blastInLabel.en, L.blastInLabel.es, lang)}</th>
                                <th style={{ width: 120 }}>{t('Minutes', 'Minutos', lang)}</th>
                                <th style={{ width: 120 }}>{t('Action', 'Acción', lang)}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {freezerCarts.length === 0 ? (
                                <tr><td colSpan={6} className="text-muted">{t(`No ${L.unitPlural.en.toLowerCase()} currently in ${L.machineLabel.en}.`, `No hay ${L.unitPlural.es.toLowerCase()} en ${L.machineLabel.es} actualmente.`, lang)}</td></tr>
                              ) : freezerCarts.map(c => {
                                const bi = c.blastInAt ? new Date(c.blastInAt) : null;
                                const mins = bi ? Math.floor((Date.now() - bi.getTime()) / 60000) : '—';
                                const name = c?.Product?.name || productById.get(Number(c.productId))?.name || `Product #${c.productId}`;
                                return (
                                  <tr key={c.id}>
                                    <td style={{ fontWeight: 900 }}>#{c.cartNumber}</td>
                                    <td>{name}</td>
                                    <td>{c.unitsInCart ?? '—'}</td>
                                    <td>{bi ? bi.toLocaleTimeString() : '—'}</td>
                                    <td>{mins}</td>
                                    <td>
                                      <button className="btn btn-outline-success" onClick={() => blastOut(c.id)} disabled={!!c.blastOutAt}>
                                        {t(L.blastOutLabel.en, L.blastOutLabel.es, lang)}
                                      </button>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                        <div className="text-muted" style={{ fontSize: 12 }}>
                          {t(`Packaging uses ${L.blastOutLabel.en} to count finished goods by product.`, `El empaque usa ${L.blastOutLabel.es} para contar los productos terminados.`, lang)}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-12 col-lg-6">
                    <div className="card">
                      <div className="card-body">
                        <div style={{ fontWeight: 900, fontSize: 16 }}>{t(`${L.blastOutLabel.en} Totals (this run)`, `Totales de ${L.blastOutLabel.es} (esta corrida)`, lang)}</div>
                        <div className="text-muted" style={{ fontSize: 12 }}>
                          {t(`Totals update as ${L.unitPlural.en.toLowerCase()} are ${isPasteurization ? 'moved to' : 'blasted'} out.`, `Los totales se actualizan al sacar los ${L.unitPlural.es.toLowerCase()}.`, lang)}
                        </div>
                        <div className="table-responsive mt-3">
                          <table className="table table-sm align-middle">
                            <thead>
                              <tr>
                                <th>{t('Product', 'Producto', lang)}</th>
                                <th style={{ width: 100 }}>{t('Total Units', 'Total Unidades', lang)}</th>
                                <th style={{ width: 160 }}></th>
                              </tr>
                            </thead>
                            <tbody>
                              {blastOutTotalsList.length === 0 ? (
                                <tr><td colSpan={3} className="text-muted">{t(`No ${L.blastOutLabel.en} totals yet.`, `Sin totales de ${L.blastOutLabel.es} aún.`, lang)}</td></tr>
                              ) : blastOutTotalsList.map(r => {
                                const labels = labelByProductId.get(r.productId) || { product: null, case: null, conflicts: [] };
                                const product = productById.get(r.productId);
                                const hasAny = labels.product || labels.case;
                                const hasConflicts = labels.conflicts?.length > 0;

                                const triggerPrint = async (tpl) => {
                                  setLoadingPrintTemplate(true);
                                  try {
                                    const fullTpl = await productLabelService.getTemplate(tpl.id);
                                    setLivePrintTarget({ product, labelTemplate: fullTpl });
                                  } catch {
                                    alert(t('Could not load label. Please try again.', 'No se pudo cargar la etiqueta. Intenta de nuevo.', lang));
                                  } finally {
                                    setLoadingPrintTemplate(false);
                                  }
                                };

                                const productExpanded = expandedProductLabels.has(r.productId);
                                const toggleProductLabel = () => setExpandedProductLabels(prev => {
                                  const next = new Set(prev);
                                  next.has(r.productId) ? next.delete(r.productId) : next.add(r.productId);
                                  return next;
                                });

                                return (
                                  <tr key={r.productId}>
                                    <td style={{ fontWeight: 800 }}>{r.name}</td>
                                    <td style={{ fontWeight: 900 }}>{r.units}</td>
                                    <td className="text-end">
                                      {!hasAny && (
                                        <Link to="/labels" className="text-muted" style={{ fontSize: 11 }}>{t('No label', 'Sin etiqueta', lang)}</Link>
                                      )}
                                      {hasAny && (
                                        <div className="d-flex flex-column align-items-end gap-1">
                                          {/* Case label — primary, always visible */}
                                          {labels.case && (
                                            <button
                                              className="btn btn-primary btn-sm"
                                              style={{ fontSize: 11 }}
                                              disabled={loadingPrintTemplate}
                                              onClick={() => triggerPrint(labels.case)}
                                            >
                                              {loadingPrintTemplate
                                                ? <span className="spinner-border spinner-border-sm" role="status" />
                                                : `🖨 ${t('Case Label', 'Etiqueta Caja', lang)}`}
                                            </button>
                                          )}
                                          {/* Product label — hidden behind a disclosure link */}
                                          {labels.product && (
                                            <div>
                                              <button
                                                className="btn btn-link btn-sm p-0 text-muted"
                                                style={{ fontSize: 10, textDecoration: 'none' }}
                                                onClick={toggleProductLabel}
                                                title={t('Show product label', 'Mostrar etiqueta de producto', lang)}
                                              >
                                                <span style={{ display: 'inline-block', transition: 'transform .15s', transform: productExpanded ? 'rotate(90deg)' : 'none' }}>▶</span>
                                                {' '}{t('Product label', 'Etiqueta Producto', lang)}
                                              </button>
                                              {productExpanded && (
                                                <div className="mt-1">
                                                  <button
                                                    className="btn btn-outline-secondary btn-sm"
                                                    style={{ fontSize: 11 }}
                                                    disabled={loadingPrintTemplate}
                                                    onClick={() => triggerPrint(labels.product)}
                                                  >
                                                    {loadingPrintTemplate
                                                      ? <span className="spinner-border spinner-border-sm" role="status" />
                                                      : `🖨 ${t('Print Product', 'Imprimir Producto', lang)}`}
                                                  </button>
                                                </div>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      )}
                                      {hasConflicts && (
                                        <div className="text-warning mt-1" style={{ fontSize: 10 }}>
                                          ⚠ {t('Multiple active labels — go to', 'Múltiples etiquetas activas — ve a', lang)} <a href="/labels" style={{ color: 'inherit' }}>{t('Labels', 'Etiquetas', lang)}</a> {t('to resolve', 'para resolver', lang)}
                                        </div>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                              {blastOutTotalsList.length > 0 && (
                                <tr>
                                  <td style={{ fontWeight: 900 }}>{t('TOTAL', 'TOTAL', lang)}</td>
                                  <td style={{ fontWeight: 900 }}>{totalUnitsBlastedOut}</td>
                                  <td />
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                        <div className="text-muted" style={{ fontSize: 12 }}>
                          {t(`If some ${L.unitPlural.en.toLowerCase()} have blank units, totals may be undercounted.`, `Si algunos ${L.unitPlural.es.toLowerCase()} tienen unidades en blanco, los totales pueden ser bajos.`, lang)}
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* Carts list */}
              <div className="col-12">
                <div className="card">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center">
                      <h6 className="mb-0">{t(L.unitPlural.en, L.unitPlural.es, lang)}</h6>
                      <div className="text-muted" style={{ fontSize: 12 }}>
                        {completedCarts.length > 0
                          ? `${t('Active:', 'Activos:', lang)} ${activeCarts.length} | ${t('Total:', 'Total:', lang)} ${carts.length}`
                          : `${t(`Total ${L.unitPlural.en.toLowerCase()}:`, `Total ${L.unitPlural.es.toLowerCase()}:`, lang)} ${carts.length}`}
                      </div>
                    </div>
                    <div className="table-responsive mt-3">
                      <table className="table table-sm align-middle">
                        <thead>
                          <tr>
                            <th style={{ width: 90 }}>{t(L.unit.en, L.unit.es, lang)}</th>
                            <th>{t('Product', 'Producto', lang)}</th>
                            <th>{t('Units', 'Unidades', lang)}</th>
                            <th>{t(L.vesselOutLabel.en, L.vesselOutLabel.es, lang)}</th>
                            <th>{t(L.blastInLabel.en, L.blastInLabel.es, lang)}</th>
                            <th>{t(L.blastOutLabel.en, L.blastOutLabel.es, lang)}</th>
                            <th style={{ width: 260 }}>{t('Actions', 'Acciones', lang)}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {activeCarts.length === 0 && completedCarts.length === 0 ? (
                            <tr>
                              <td colSpan={7} className="text-muted">{t(`No ${L.unitPlural.en.toLowerCase()} yet.`, `Sin ${L.unitPlural.es.toLowerCase()} aún.`, lang)}</td>
                            </tr>
                          ) : activeCarts.length === 0 ? (
                            <tr>
                              <td colSpan={7} className="text-muted">{t(`All ${L.unitPlural.en.toLowerCase()} have been processed.`, `Todos los ${L.unitPlural.es.toLowerCase()} han sido procesados.`, lang)}</td>
                            </tr>
                          ) : activeCarts.map((c) => {
                            const ovenOut = c.ovenOutAt ? new Date(c.ovenOutAt) : null;
                            const blastInAt = c.blastInAt ? new Date(c.blastInAt) : null;
                            const blastOutAt = c.blastOutAt ? new Date(c.blastOutAt) : null;
                            const freezerOverdue = (() => {
                              if (!ovenOut) return false;
                              if (blastOutAt) return false;
                              return Math.floor((Date.now() - ovenOut.getTime()) / 60000) > maxMinutesToFreezer;
                            })();
                            const prodName = c?.Product?.name || productById.get(Number(c.productId))?.name || '—';
                            return (
                              <tr key={c.id}>
                                <td style={{ fontWeight: 900 }}>#{c.cartNumber}</td>
                                <td>{prodName}</td>
                                <td>{c.unitsInCart ?? '—'}</td>
                                <td>{ovenOut ? ovenOut.toLocaleTimeString() : '—'}</td>
                                <td>{blastInAt ? blastInAt.toLocaleTimeString() : '—'}</td>
                                <td>
                                  {blastOutAt ? blastOutAt.toLocaleTimeString() : (
                                    freezerOverdue ? <span className="badge bg-danger">{t('OVERDUE', 'VENCIDO', lang)}</span> : '—'
                                  )}
                                </td>
                                <td>
                                  <div className="d-flex gap-2">
                                    <button
                                      className="btn btn-outline-secondary btn-sm"
                                      onClick={() => openEditCart(c)}
                                      disabled={!!c.blastInAt || isVerified || isCompleted}
                                      title={c.blastInAt ? t(`Cannot edit after ${L.blastInLabel.en}`, `No se puede editar después de ${L.blastInLabel.es}`, lang) : t(`Edit ${L.unit.en.toLowerCase()}`, `Editar ${L.unit.es.toLowerCase()}`, lang)}
                                    >
                                      ✏️
                                    </button>
                                    <button
                                      className="btn btn-outline-secondary btn-sm"
                                      onClick={() => openEditTimes(c)}
                                      disabled={isVerified || isCompleted}
                                      title={t('Correct a recorded time (e.g. logged late)', 'Corregir un horario registrado (ej. registrado tarde)', lang)}
                                    >
                                      ⏱️
                                    </button>
                                    <button
                                      className="btn btn-outline-primary"
                                      onClick={() => blastIn(c.id)}
                                      disabled={!!c.blastInAt || isVerified || isCompleted}
                                    >
                                      {t(L.blastInLabel.en, L.blastInLabel.es, lang)}
                                    </button>
                                    <button
                                      className="btn btn-outline-success"
                                      onClick={() => blastOut(c.id)}
                                      disabled={!c.blastInAt || !!c.blastOutAt || isVerified || isCompleted}
                                      title={!c.blastInAt ? t(`Record ${L.blastInLabel.en} first`, `Registra ${L.blastInLabel.es} primero`, lang) : undefined}
                                    >
                                      {t(L.blastOutLabel.en, L.blastOutLabel.es, lang)}
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {completedCarts.length > 0 && (
                      <div className="mt-2">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => setShowCompletedCarts(v => !v)}
                        >
                          {showCompletedCarts
                            ? `▼ ${t('Hide', 'Ocultar', lang)} ${completedCarts.length} ${t(`completed ${L.unit.en.toLowerCase()}`, `${L.unit.es.toLowerCase()} completado`, lang)}${completedCarts.length !== 1 ? (lang === 'es' ? 's' : 's') : ''}`
                            : `▶ ${t('Show', 'Mostrar', lang)} ${completedCarts.length} ${t(`completed ${L.unit.en.toLowerCase()}`, `${L.unit.es.toLowerCase()} completado`, lang)}${completedCarts.length !== 1 ? 's' : ''}`}
                        </button>
                        {showCompletedCarts && (
                          <div className="table-responsive mt-2" style={{ opacity: 0.6 }}>
                            <table className="table table-sm align-middle">
                              <thead>
                                <tr>
                                  <th style={{ width: 90 }}>{t(L.unit.en, L.unit.es, lang)}</th>
                                  <th>{t('Product', 'Producto', lang)}</th>
                                  <th>{t('Units', 'Unidades', lang)}</th>
                                  <th>{t(L.vesselOutLabel.en, L.vesselOutLabel.es, lang)}</th>
                                  <th>{t(L.blastInLabel.en, L.blastInLabel.es, lang)}</th>
                                  <th>{t(L.blastOutLabel.en, L.blastOutLabel.es, lang)}</th>
                                  <th style={{ width: 110 }}>{t(`${L.machineLabel.en[0].toUpperCase()}${L.machineLabel.en.slice(1)} (min)`, `${L.machineLabel.es[0].toUpperCase()}${L.machineLabel.es.slice(1)} (min)`, lang)}</th>
                                  <th style={{ width: 70 }}></th>
                                </tr>
                              </thead>
                              <tbody>
                                {completedCarts.map((c) => {
                                  const ovenOut = c.ovenOutAt ? new Date(c.ovenOutAt) : null;
                                  const blastInAt = c.blastInAt ? new Date(c.blastInAt) : null;
                                  const blastOutAt = c.blastOutAt ? new Date(c.blastOutAt) : null;
                                  const freezerMins = blastInAt && blastOutAt
                                    ? Math.round((blastOutAt.getTime() - blastInAt.getTime()) / 60000)
                                    : null;
                                  const prodName = c?.Product?.name || productById.get(Number(c.productId))?.name || '—';
                                  return (
                                    <tr key={c.id}>
                                      <td style={{ fontWeight: 900 }}>#{c.cartNumber}</td>
                                      <td>{prodName}</td>
                                      <td>{c.unitsInCart ?? '—'}</td>
                                      <td>{ovenOut ? ovenOut.toLocaleTimeString() : '—'}</td>
                                      <td>{blastInAt ? blastInAt.toLocaleTimeString() : '—'}</td>
                                      <td>{blastOutAt ? blastOutAt.toLocaleTimeString() : '—'}</td>
                                      <td style={{ fontWeight: 700 }}>{freezerMins != null ? `${freezerMins} min` : '—'}</td>
                                      <td className="d-flex align-items-center gap-2">
                                        <span className="badge text-bg-success">{t('Done', 'Listo', lang)}</span>
                                        <button
                                          className="btn btn-outline-secondary btn-sm"
                                          onClick={() => openEditTimes(c)}
                                          disabled={isVerified}
                                          title={t('Correct a recorded time (e.g. logged late)', 'Corregir un horario registrado (ej. registrado tarde)', lang)}
                                        >
                                          ⏱️
                                        </button>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    )}

                    <div className="text-muted" style={{ fontSize: 12 }}>
                      {t(`SQF intent: ${L.unitPlural.en.toLowerCase()} provide a production cross-check and ensure ${L.machineLabel.en} timing control.`, `Intención SQF: los ${L.unitPlural.es.toLowerCase()} proporcionan verificación de producción y control de tiempo en ${L.machineLabel.es}.`, lang)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {!run && !loading && !error && (
          <div className="text-muted mt-3">{t('Run not found.', 'Corrida no encontrada.', lang)}</div>
        )}

        {/* STOP PASTEURIZING MODAL — final temperature required */}
        {showStopPasteurizeModal && (
          <div className="modal d-block" tabIndex="-1" role="dialog" style={{ background: 'rgba(0,0,0,0.45)' }}>
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{t(L.stopStage1Button.en, L.stopStage1Button.es, lang)}</h5>
                  <button type="button" className="btn-close" onClick={() => setShowStopPasteurizeModal(false)} disabled={savingStop} />
                </div>
                <div className="modal-body">
                  {error && <div className="alert alert-danger py-2 mb-3" style={{ fontSize: 13 }}>{error}</div>}
                  <label className="form-label mb-1">{t(L.finalTempLabel.en, L.finalTempLabel.es, lang)}</label>
                  <input
                    type="number"
                    className="form-control"
                    value={stopTempF}
                    onChange={(e) => setStopTempF(e.target.value)}
                    placeholder="e.g. 155"
                    style={{ fontSize: 22, fontWeight: 800, padding: '14px 12px' }}
                    inputMode="numeric"
                    autoFocus
                  />
                  <div className="mt-2 d-flex flex-wrap gap-2">
                    {L.vesselTempQuick.map(v => (
                      <button key={v} type="button" className="btn btn-outline-secondary" onClick={() => setStopTempF(String(v))}>
                        {v}
                      </button>
                    ))}
                  </div>
                  <div className="text-muted mt-2" style={{ fontSize: 12 }}>
                    {t('Confirms the batch held at/above the critical temperature for the full hold. Required to close out this CCP.', 'Confirma que el lote se mantuvo a la temperatura crítica o por encima durante todo el sostenimiento. Requerido para cerrar este PCC.', lang)}
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-outline-secondary" onClick={() => setShowStopPasteurizeModal(false)} disabled={savingStop}>
                    {t('Cancel', 'Cancelar', lang)}
                  </button>
                  <button className="btn btn-danger" onClick={confirmStopPasteurizing} disabled={savingStop}>
                    {savingStop ? t('Saving…', 'Guardando…', lang) : t(L.stopStage1Button.en, L.stopStage1Button.es, lang)}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* TEMP MODAL */}
        {showTempModal && (
          <div className="modal d-block" tabIndex="-1" role="dialog" style={{ background: 'rgba(0,0,0,0.45)' }}>
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    {isPasteurization
                      ? t('Record Mix Temperature Check', 'Registrar Control de Temperatura de Mezcla', lang)
                      : t('Record Temperature', 'Registrar Temperatura', lang)}
                  </h5>
                  <button type="button" className="btn-close" onClick={() => setShowTempModal(false)} />
                </div>
                <div className="modal-body">
                  {isPasteurization && (
                    <div className="text-muted mb-2" style={{ fontSize: 12 }}>
                      {t(L.intervalCheckNote.en, L.intervalCheckNote.es, lang)}
                    </div>
                  )}
                  <label className="form-label mb-1">{t('Temperature (°F)', 'Temperatura (°F)', lang)}</label>
                  <input
                    type="number"
                    className="form-control"
                    value={tempF}
                    onChange={(e) => setTempF(e.target.value)}
                    placeholder="e.g. 210"
                    style={{ fontSize: 22, fontWeight: 800, padding: '14px 12px' }}
                    inputMode="numeric"
                    disabled={!isBaking}
                  />
                  <div className="mt-2 d-flex flex-wrap gap-2">
                    {tempQuick.map(v => (
                      <button
                        key={v}
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => setTempF(String(v))}
                        disabled={!isBaking}
                      >
                        {v}
                      </button>
                    ))}
                  </div>
                  {!isPasteurization && (
                    <div className="mt-3">
                      <label className="form-label mb-1">{t(`Tie to ${L.unit.en.toLowerCase()} (optional)`, `Asociar a ${L.unit.es.toLowerCase()} (opcional)`, lang)}</label>
                      <select
                        className="form-select"
                        value={tempCartId}
                        onChange={(e) => setTempCartId(e.target.value)}
                        disabled={!isBaking}
                      >
                        <option value="">{t(`Product Temperature (no ${L.unit.en.toLowerCase()})`, `Temperatura de Producto (sin ${L.unit.es.toLowerCase()})`, lang)}</option>
                        {[...carts.filter(c => !c?.blastInAt)].reverse().map(c => (
                          <option key={c.id} value={c.id}>{L.unit.en} #{c.cartNumber}</option>
                        ))}
                      </select>
                      <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                        {t(`If you measured a specific ${L.unit.en.toLowerCase()}, select it; otherwise leave blank.`, `Si mediste un ${L.unit.es.toLowerCase()} específico, selecciónalo; si no, déjalo en blanco.`, lang)}
                      </div>
                    </div>
                  )}
                  {!isBaking && (
                    <div className="text-muted mt-3" style={{ fontSize: 12 }}>
                      {t('Temperature recording is disabled unless status is', 'El registro de temperatura está desactivado a menos que el estado sea', lang)} <b>{lang === 'es' ? L.stage1Verb.es : L.stage1Verb.en}</b>.
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button className="btn btn-outline-secondary" onClick={() => setShowTempModal(false)} disabled={savingTemp}>
                    {t('Cancel', 'Cancelar', lang)}
                  </button>
                  <button className="btn btn-primary" onClick={saveTemp} disabled={savingTemp || !isBaking}>
                    {savingTemp ? t('Saving…', 'Guardando…', lang) : t('Save Temperature', 'Guardar Temperatura', lang)}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* CART MODAL */}
        {showCartModal && (
          <div className="modal d-block" tabIndex="-1" role="dialog" style={{ background: 'rgba(0,0,0,0.45)' }}>
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className={`modal-header ${editingCartId ? 'bg-warning' : ''}`}>
                  <h5 className="modal-title">{editingCartId ? t(`Edit ${L.unit.en}`, `Editar ${L.unit.es}`, lang) : t(`New ${L.unit.en}`, `Nuevo ${L.unit.es}`, lang)}</h5>
                  <button type="button" className="btn-close" onClick={() => { setShowCartModal(false); setEditingCartId(null); }} />
                </div>
                <div className="modal-body">
                  {error && <div className="alert alert-danger py-2 mb-3" style={{ fontSize: 13 }}>{error}</div>}
                  {scheduledProducts.length > 0 && (
                    <div className="mb-3">
                      <label className="form-label mb-2 fw-semibold">{t("Today's Schedule", 'Horario de Hoy', lang)}</label>
                      <div className="d-flex flex-wrap gap-2">
                        {scheduledProducts.map(p => (
                          <button
                            key={p.id}
                            type="button"
                            className={`btn btn-lg ${Number(cartProductId) === p.id ? 'btn-primary' : 'btn-outline-primary'}`}
                            style={{ fontWeight: 700 }}
                            onClick={() => {
                              setCartProductId(String(p.id));
                              if (p.defaultUnitsPerCart != null) setCartUnits(String(p.defaultUnitsPerCart));
                            }}
                          >
                            {p.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <label className="form-label mb-1">
                    {scheduledProducts.length > 0 ? t('Other product', 'Otro producto', lang) : t('Product', 'Producto', lang)}
                  </label>
                  <select
                    className="form-select"
                    value={cartProductId}
                    onChange={(e) => setCartProductId(e.target.value)}
                  >
                    <option value="">{t('Select product', 'Seleccionar producto', lang)}</option>
                    {flowProducts.map(p => (
                      <option key={p.id} value={p.id}>
                        {p.name}{p.defaultUnitsPerCart != null ? ` (default ${p.defaultUnitsPerCart})` : ''}
                      </option>
                    ))}
                  </select>

                  <div className="mt-3">
                    <label className="form-label mb-1">{t(`Units in ${L.unit.en.toLowerCase()}`, `Unidades en el ${L.unit.es.toLowerCase()}`, lang)}</label>
                    <input
                      type="number"
                      className="form-control"
                      value={cartUnits}
                      onChange={(e) => setCartUnits(e.target.value)}
                      placeholder="e.g. 40"
                      style={{ fontSize: 18, fontWeight: 800, padding: '12px 12px' }}
                      inputMode="numeric"
                    />
                    <div className="mt-2 d-flex flex-wrap gap-2">
                      {unitsQuick.map(v => (
                        <button
                          key={v}
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={() => setCartUnits(String(v))}
                        >
                          {v}
                        </button>
                      ))}
                    </div>
                    <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                      {t("Default uses the product's configured default quantity when available.", 'El valor predeterminado usa la cantidad configurada del producto cuando está disponible.', lang)}
                    </div>
                  </div>

                  <div className="mt-3">
                    <label className="form-label mb-1">{t('Notes (optional)', 'Notas (opcional)', lang)}</label>
                    <input
                      className="form-control"
                      value={cartNotes}
                      onChange={(e) => setCartNotes(e.target.value)}
                      placeholder={t('Quick note', 'Nota rápida', lang)}
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-outline-secondary" onClick={() => { setShowCartModal(false); setEditingCartId(null); }} disabled={savingCart}>
                    {t('Cancel', 'Cancelar', lang)}
                  </button>
                  <button className={`btn ${editingCartId ? 'btn-warning' : 'btn-primary'}`} onClick={createCart} disabled={savingCart}>
                    {savingCart ? t('Saving…', 'Guardando…', lang) : editingCartId ? t(`Update ${L.unit.en}`, `Actualizar ${L.unit.es}`, lang) : t(`Create ${L.unit.en}`, `Crear ${L.unit.es}`, lang)}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* EDIT TIMES MODAL — correct ovenOutAt/blastInAt/blastOutAt after the fact */}
        {editTimesCart && (
          <div className="modal d-block" tabIndex="-1" role="dialog" style={{ background: 'rgba(0,0,0,0.45)' }}>
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{t(`Edit Times — ${L.unit.en} #${editTimesCart.cartNumber}`, `Editar Horarios — ${L.unit.es} #${editTimesCart.cartNumber}`, lang)}</h5>
                  <button type="button" className="btn-close" onClick={() => setEditTimesCart(null)} disabled={savingEditTimes} />
                </div>
                <div className="modal-body">
                  {error && <div className="alert alert-danger py-2 mb-3" style={{ fontSize: 13 }}>{error}</div>}
                  <div className="mb-3">
                    <label className="form-label mb-1">{t(L.vesselOutLabel.en, L.vesselOutLabel.es, lang)}</label>
                    <input
                      type="datetime-local"
                      className="form-control"
                      value={editTimesForm.ovenOutAt}
                      onChange={(e) => setEditTimesForm(f => ({ ...f, ovenOutAt: e.target.value }))}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label mb-1">{t(L.blastInLabel.en, L.blastInLabel.es, lang)}</label>
                    <input
                      type="datetime-local"
                      className="form-control"
                      value={editTimesForm.blastInAt}
                      onChange={(e) => setEditTimesForm(f => ({ ...f, blastInAt: e.target.value }))}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label mb-1">{t(L.blastOutLabel.en, L.blastOutLabel.es, lang)}</label>
                    <input
                      type="datetime-local"
                      className="form-control"
                      value={editTimesForm.blastOutAt}
                      onChange={(e) => setEditTimesForm(f => ({ ...f, blastOutAt: e.target.value }))}
                    />
                  </div>
                  <div className="mb-1">
                    <label className="form-label mb-1">{t('Reason for correction (required)', 'Motivo de la corrección (requerido)', lang)}</label>
                    <textarea
                      className="form-control"
                      rows={2}
                      value={editTimesNote}
                      onChange={(e) => setEditTimesNote(e.target.value)}
                      placeholder={t('e.g. forgot to log at the time, logging now from memory', 'ej. olvidé registrar en el momento, registrando ahora de memoria', lang)}
                    />
                  </div>
                  <div className="text-muted" style={{ fontSize: 11 }}>
                    {t('Edits are logged with your name and this reason for the SQF record.', 'Las ediciones quedan registradas con tu nombre y este motivo para el registro SQF.', lang)}
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-outline-secondary" onClick={() => setEditTimesCart(null)} disabled={savingEditTimes}>
                    {t('Cancel', 'Cancelar', lang)}
                  </button>
                  <button className="btn btn-warning" onClick={saveEditTimes} disabled={savingEditTimes}>
                    {savingEditTimes ? t('Saving…', 'Guardando…', lang) : t('Save Times', 'Guardar Horarios', lang)}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* BLOCKING CARTS MODAL */}
        {showBlockingModal && (
          <div className="modal d-block" tabIndex="-1" role="dialog" style={{ background: 'rgba(0,0,0,0.55)' }}>
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header" style={{ background: '#ffc107' }}>
                  <h5 className="modal-title">{t('CANNOT CLOSE PRODUCTION', 'NO SE PUEDE CERRAR LA PRODUCCIÓN', lang)}</h5>
                  <button type="button" className="btn-close" onClick={() => setShowBlockingModal(false)} />
                </div>
                <div className="modal-body">

                     {hasBlockingCarts && (
                    <div className="alert alert-warning mt-3 mb-0">
                      <div style={{ fontWeight: 900, fontSize: 15 }}>
                        ⚠️ {t(`Production cannot be closed until all ${L.unitPlural.en.toLowerCase()} have completed the ${L.cycleLabel.en} cycle.`, `La producción no puede cerrarse hasta que todos los ${L.unitPlural.es.toLowerCase()} hayan completado el ciclo de ${L.cycleLabel.es}.`, lang)}
                      </div>
                      <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                        {t(`Every ${L.unit.en.toLowerCase()} must go through ${L.blastInLabel.en} and ${L.blastOutLabel.en} before the run can be finalized.`, `Cada ${L.unit.es.toLowerCase()} debe pasar por ${L.blastInLabel.es} y ${L.blastOutLabel.es} antes de finalizar la corrida.`, lang)}
                      </div>

                      {cartsNotYetFrozen.length > 0 && (
                        <div className="mt-2">
                          <div style={{ fontWeight: 800, fontSize: 13 }}>{t(`Not yet sent to ${L.machineLabel.en}:`, `Aún no enviados a ${L.machineLabel.es}:`, lang)}</div>
                          <ul className="mb-0 mt-1" style={{ fontSize: 13 }}>
                            {cartsNotYetFrozen.map(c => {
                              const name = c?.Product?.name || productById.get(Number(c.productId))?.name || `Product #${c.productId}`;
                              return (
                                <li key={c.id}>
                                  {L.unit.en} #{c.cartNumber} — {name} {c.unitsInCart != null ? `(${c.unitsInCart} ${t('units', 'unidades', lang)})` : ''}
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      )}

                      {cartsStillInFreezer.length > 0 && (
                        <div className="mt-2">
                          <div style={{ fontWeight: 800, fontSize: 13 }}>{t(`Still inside the ${L.machineLabel.en} (${L.blastOutLabel.en} pending):`, `Aún dentro de ${L.machineLabel.es} (${L.blastOutLabel.es} pendiente):`, lang)}</div>
                          <ul className="mb-0 mt-1" style={{ fontSize: 13 }}>
                            {cartsStillInFreezer.map(c => {
                              const name = c?.Product?.name || productById.get(Number(c.productId))?.name || `Product #${c.productId}`;
                              const bi = c.blastInAt ? new Date(c.blastInAt).toLocaleTimeString() : '—';
                              return (
                                <li key={c.id}>
                                  {L.unit.en} #{c.cartNumber} — {name} {c.unitsInCart != null ? `(${c.unitsInCart} ${t('units', 'unidades', lang)})` : ''} · {t(`${L.blastInLabel.en}:`, `${L.blastInLabel.es}:`, lang)} {bi}
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button className="btn btn-primary" onClick={() => setShowBlockingModal(false)}>
                    {t('CLOSE', 'CERRAR', lang)}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* COMPLETE PRODUCTION MODAL */}
        {showCompleteModal && (
          <div className="modal d-block" tabIndex="-1" role="dialog" style={{ background: 'rgba(0,0,0,0.55)' }}>
            <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{t('Complete Production (Close Run)', 'Completar Producción (Cerrar Corrida)', lang)}</h5>
                  <button type="button" className="btn-close" onClick={() => setShowCompleteModal(false)} />
                </div>

                <div className="modal-body">
                  <div className="alert alert-secondary" style={{ fontSize: 13 }}>
                    {t('Requirement: baking must be', 'Requisito: el horneado debe estar', lang)} <b>STOPPED</b>. {t('Checklist questions are required to close the batch for the day.', 'Las preguntas de la lista son obligatorias para cerrar el lote del día.', lang)}
                  </div>

                  <div className="row g-2">
                    <div className="col-12 col-md-6">
                      <label className="form-label mb-1">{t('Finished Goods Lot Code', 'Código de Lote de Producto Terminado', lang)} <span className="text-danger">*</span></label>
                      <input
                        className="form-control"
                        value={finLot}
                        readOnly
                        disabled
                      />
                      <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                        {t('Auto-filled from the Batch lot code created at run start.', 'Auto-llenado desde el código de lote del lote creado al inicio de la corrida.', lang)}
                      </div>
                    </div>

                    <div className="col-12 col-md-6">
                      <label className="form-label mb-1">{t('Total Units Produced', 'Total de Unidades Producidas', lang)}</label>
                      <input
                        className="form-control"
                        value={editedTotalUnits}
                        readOnly
                        disabled
                      />
                      <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                        {t('Auto-calculated from the editable product totals below.', 'Calculado automáticamente de los totales de productos editables abajo.', lang)}
                      </div>
                    </div>
                  </div>

                  <div className="mt-3">
                    <div style={{ fontWeight: 900 }}>{t('Units by Product Type', 'Unidades por Tipo de Producto', lang)}</div>
                    <div className="text-muted" style={{ fontSize: 12 }}>
                      {t(`Pre-filled from ${L.blastOutLabel.en} totals. Edit if actual counts differ.`, `Pre-llenado de totales de ${L.blastOutLabel.es}. Edita si los conteos reales difieren.`, lang)}
                    </div>

                    <div className="table-responsive mt-2">
                      <table className="table table-sm align-middle">
                        <thead>
                          <tr>
                            <th>{t('Product', 'Producto', lang)}</th>
                            <th style={{ width: 160 }}>
                              {t('Units Produced', 'Unidades Producidas', lang)}
                              <div className="text-muted fw-normal" style={{ fontSize: 10 }}>
                                {t('Include discarded in count', 'Incluir descartadas en el conteo', lang)}
                              </div>
                            </th>
                            <th style={{ width: 160 }}>{t('Discarded', 'Descartadas', lang)}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {editableProductTotals.length === 0 ? (
                            <tr><td colSpan={3} className="text-muted">{t('No Blast Out totals yet.', 'Sin totales de Salida Congelador aún.', lang)}</td></tr>
                          ) : (
                            <>
                              {editableProductTotals.map((r, idx) => (
                                <tr key={r.productId}>
                                  <td style={{ fontWeight: 800 }}>{r.name}</td>
                                  <td>
                                    <input
                                      type="number"
                                      className="form-control form-control-sm"
                                      value={r.units}
                                      min={0}
                                      onChange={(e) => {
                                        const val = e.target.value === '' ? '' : Number(e.target.value);
                                        setEditableProductTotals(prev =>
                                          prev.map((x, i) => i === idx ? { ...x, units: val } : x)
                                        );
                                      }}
                                    />
                                  </td>
                                  <td>
                                    <input
                                      type="number"
                                      className="form-control form-control-sm"
                                      value={r.discarded ?? 0}
                                      min={0}
                                      onChange={(e) => {
                                        const val = e.target.value === '' ? 0 : Number(e.target.value);
                                        setEditableProductTotals(prev =>
                                          prev.map((x, i) => i === idx ? { ...x, discarded: val } : x)
                                        );
                                      }}
                                    />
                                  </td>
                                </tr>
                              ))}
                              <tr style={{ borderTop: '2px solid #dee2e6' }}>
                                <td style={{ fontWeight: 900 }}>{t('TOTAL', 'TOTAL', lang)}</td>
                                <td style={{ fontWeight: 900 }}>{editedTotalUnits}</td>
                                <td style={{ fontWeight: 900 }}>{editedTotalDiscarded > 0 ? editedTotalDiscarded : '—'}</td>
                              </tr>
                            </>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="mt-3">
                    <div style={{ fontWeight: 900 }}>{t('Packaging Checklist', 'Lista de Verificación de Empaque', lang)}</div>
                    <div className="text-muted" style={{ fontSize: 12 }}>
                      {t('All required questions must be answered to complete.', 'Todas las preguntas requeridas deben responderse para completar.', lang)}
                    </div>
                    <div className="mt-2">
                      {normalizedQuestions.length === 0 ? (
                        <div className="text-muted">{t('No questions configured.', 'No hay preguntas configuradas.', lang)}</div>
                      ) : normalizedQuestions.map(q => (
                        <div key={q.key} className="border rounded p-2 mb-2">
                          <div style={{ fontWeight: 800 }}>
                            {q.question} {q.required && <span className="text-danger">*</span>}
                          </div>

                          {q.type === 'CHECK' && (
                            <div className="form-check mt-2">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                checked={answers[q.key] === true}
                                onChange={(e) => setAnswers(prev => ({ ...prev, [q.key]: e.target.checked }))}
                              />
                              <label className="form-check-label">{t('Confirmed', 'Confirmado', lang)}</label>
                            </div>
                          )}

                          {q.type === 'TEXT' && (
                            <input
                              className="form-control mt-2"
                              value={answers[q.key] || ''}
                              onChange={(e) => setAnswers(prev => ({ ...prev, [q.key]: e.target.value }))}
                              placeholder={t('Enter answer', 'Ingresa respuesta', lang)}
                            />
                          )}

                          {q.type === 'NUMBER' && (
                            <input
                              type="number"
                              className="form-control mt-2"
                              value={answers[q.key] ?? ''}
                              onChange={(e) => setAnswers(prev => ({ ...prev, [q.key]: e.target.value }))}
                              placeholder={t('Enter number', 'Ingresa número', lang)}
                            />
                          )}

                          {q.type === 'SELECT' && (
                            <select
                              className="form-select mt-2"
                              value={answers[q.key] ?? ''}
                              onChange={(e) => setAnswers(prev => ({ ...prev, [q.key]: e.target.value }))}
                            >
                              <option value="">{t('Select…', 'Seleccionar…', lang)}</option>
                              {q.options.map(opt => (
                                <option key={String(opt)} value={String(opt)}>{String(opt)}</option>
                              ))}
                            </select>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="mt-3">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={deviation}
                        onChange={(e) => setDeviation(e.target.checked)}
                        id="devCheck"
                      />
                      <label className="form-check-label" htmlFor="devCheck">
                        {t('Deviation occurred', 'Ocurrió una desviación', lang)}
                      </label>
                    </div>

                    {deviation && (
                      <div className="row g-2 mt-2">
                        <div className="col-12">
                          <label className="form-label mb-1">{t('Deviation Notes', 'Notas de Desviación', lang)}</label>
                          <textarea className="form-control" value={deviationNotes} onChange={(e) => setDeviationNotes(e.target.value)} />
                        </div>
                        <div className="col-12">
                          <label className="form-label mb-1">{t('Corrective Action Notes', 'Notas de Acción Correctiva', lang)}</label>
                          <textarea className="form-control" value={correctiveNotes} onChange={(e) => setCorrectiveNotes(e.target.value)} />
                        </div>
                      </div>
                    )}

                    <div className="mt-2">
                      <label className="form-label mb-1">{t('Packaging Notes (optional)', 'Notas de Empaque (opcional)', lang)}</label>
                      <textarea className="form-control" value={packNotes} onChange={(e) => setPackNotes(e.target.value)} />
                    </div>
                  </div>
                </div>

                <div className="modal-footer">
                  <div className="me-auto text-muted" style={{ fontSize: 12 }}>
                    {allRequiredAnswered ? t('Checklist ready.', 'Lista lista.', lang) : t('Checklist incomplete.', 'Lista incompleta.', lang)}
                  </div>
                  <button className="btn btn-outline-secondary" onClick={() => setShowCompleteModal(false)} disabled={savingComplete}>
                    {t('Cancel', 'Cancelar', lang)}
                  </button>
                  <div className="d-flex flex-column align-items-end">
                    <button
                      className="btn btn-success"
                      onClick={doComplete}
                      disabled={savingComplete || !isStopped || !allRequiredAnswered}
                      title={!isStopped ? (isNoBake ? t('Stop Producing first.', 'Detén la producción primero.', lang) : t(`${L.stopStage1Button.en} first.`, `${L.stopStage1Button.es} primero.`, lang)) : (!allRequiredAnswered ? t('Answer all required questions.', 'Responde todas las preguntas requeridas.', lang) : '')}
                    >
                      {savingComplete ? t('Saving…', 'Guardando…', lang) : t('Complete Production', 'Completar Producción', lang)}
                    </button>
                    <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                      {t('Complete production and send to QA for verification and validation.', 'Completar producción y enviar a QA para verificación y validación.', lang)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>

    {/* LIVE PRINT MODAL */}
    <LivePrintModal
      show={!!livePrintTarget}
      onClose={() => setLivePrintTarget(null)}
      product={livePrintTarget?.product}
      lotCode={runLot !== '—' ? runLot : ''}
      productionDate={runProdDate !== '—' ? runProdDate : ''}
      labelTemplate={livePrintTarget?.labelTemplate}
      initialDateFormat={
        lastUsedDateFormats.current[livePrintTarget?.labelTemplate?.id]
        || livePrintTarget?.labelTemplate?.dateFormat
        || 'MM/DD/YYYY'
      }
      onPrint={handleLivePrint}
    />

    {/* PORTAL-BASED PRINT AREA */}
    {printJob && (() => {
      const tpl = printJob.labelTemplate;
      const activeVersion = tpl?.Versions?.find(v => v.id === tpl.activeVersionId);
      const { design, content } = splitSnapshot(activeVersion?.snapshot);
      if (!design) return null;
      const w = parseFloat(design.width) || 6;
      const h = parseFloat(design.height) || 4;
      const expLabelRaw = tpl?.expirationLabel || 'best_by';
      const expLabel = EXP_LABEL_DISPLAY[expLabelRaw] || expLabelRaw;
      const dateFmt = printJob.dateFormat || tpl?.dateFormat || 'MM/DD/YYYY';
      const expDateFmt = printJob.expirationDate ? formatLabelDate(printJob.expirationDate, dateFmt) : '';
      const prodDateFmt = printJob.productionDate ? formatLabelDate(printJob.productionDate, dateFmt) : '';
      const variant = printJob.variantId != null
        ? (tpl.Variants || []).find(v => Number(v.id) === Number(printJob.variantId))
        : null;
      // Effective expiration label: variant can override the template-level setting
      const effectiveExpLabel = (variant?.expLabelOverride?.trim()) || expLabel;
      const printContent = {
        ...content,
        logoUrl: companyLogoUrl || content.logoUrl,
        lot: printJob.lotCode ? `Lot: ${printJob.lotCode}` : content.lot,
        exp: expDateFmt ? `${effectiveExpLabel}: ${expDateFmt}` : content.exp,
        expirationLabel: effectiveExpLabel + ':',
        prodDate: prodDateFmt,
        productId: tpl.productId || null,
        ...(variant?.labelTitle   != null && variant.labelTitle   !== '' && { productName: variant.labelTitle }),
        ...(variant?.netWeight    != null && variant.netWeight    !== '' && { netWeight: variant.netWeight }),
        ...(variant?.upc          != null && variant.upc          !== '' && { upc: variant.upc, caseUpc: variant.upc }),
        ...(variant?.logoOverride != null && variant.logoOverride !== '' && { logoUrl: variant.logoOverride }),
        ...(variant?.prodDateDisplay === 'show' && { showProdDate: true }),
        ...(variant?.prodDateDisplay === 'hide' && { showProdDate: false }),
        ...(variant?.prodDateLabel   != null && variant.prodDateLabel !== '' && { prodDateLabel: variant.prodDateLabel }),
        ...(variant != null && variant.expBlockTitle != null && { expBlockTitle: variant.expBlockTitle }),
      };
      const copies = printJob.copies || 1;
      return ReactDOM.createPortal(
        <>
          <style>{`@media print { #root { display: none !important; } #live-print-portal { display: block !important; } @page { margin: 0; size: ${w}in ${h}in; } }`}</style>
          <div id="live-print-portal" style={{ display: 'none' }}>
            {Array.from({ length: copies }, (_, i) => (
              <div key={i} style={{ pageBreakAfter: i < copies - 1 ? 'always' : 'auto', width: `${w}in`, height: `${h}in`, overflow: 'hidden' }}>
                <LabelRenderer design={design} content={printContent} scale={1} isDesign={false} />
              </div>
            ))}
          </div>
        </>,
        document.body
      );
    })()}

    {showVisualInspectionModal && (
      <StartVisualInspectionModal
        defaultBatchId={run?.batchId}
        batchLabel={run?.Batch?.lotCode || `Batch #${run?.batchId}`}
        productOptions={scheduledProducts.map(p => ({ id: p.id, name: p.name }))}
        onClose={() => setShowVisualInspectionModal(false)}
      />
    )}

    </>
  );
}